import { defineStore } from 'pinia'
import type { ChatMessage, ChatSession } from '@/types/chat'
import { MessageType, MessageStatus } from '@/types/chat'
import SendBurialPoint from '@/utils/sendBurialPoint'
const sendBurialPoint = new SendBurialPoint()
import { MessageAPI } from '@/services/message'
import { chatRecordsDB } from '@/utils/chatRecordsDB'
import type {
  ChatRecordDigitalHuman,
  ChatRecordSession,
  ChatRecordMessage
} from '@/utils/chatRecordsDB'
import { useStoreI18n, I18N_KEYS } from '@/composables/useI18nStore'
import { useLanguage } from '@/utils/language'
import { timeoutPromptWord } from '@/types/chat'
import { getSSEManager } from '@/utils/sse'

export interface DigitalHuman {
  id: string
  name: string
  avatar: string
  sessions: ChatSession[]
  currentSessionId: string | null
  lastSyncTime?: number // 最后同步时间
  isFromAPI?: boolean // 是否来自API
  unreadCount?: number // 🔧 新增：未读消息数量
  lastActiveTime?: number // 🔧 新增：最后活跃时间（用户最后查看的时间）
  isLoading?: boolean // 🔧 新增：该数字人的加载状态
  isTyping?: boolean // 🔧 新增：该数字人的打字状态
  // 🔧 新增：该数字人是否有正在进行的生图任务（用于切换数字人时保护状态）
  hasActiveImageTask?: boolean
  // 🔧 新增：是否开启全局翻译
  isGlobalTranslationEnabled?: boolean
}

// 🔧 优化：添加常量定义
const SSE_TIMEOUT_MS = 30000 // 30秒消息回复超时，超时后开始轮询拉取消息记录（给AI生成留足时间）
const MESSAGE_VALIDATION_WINDOW_MS = 10 * 60 * 1000 // 10分钟消息验证窗口，避免长回复被误丢弃

// 🔧 优化：防抖定时器（避免频繁保存）
let saveDebounceTimer: ReturnType<typeof setTimeout> | null = null
const SAVE_DEBOUNCE_MS = 500 // 500ms 防抖

export interface ChatState {
  // 自动翻译中
  translateMessageLoading: any
  // 最新发送的消息ID
  latestSentMessageId: string | null
  // 所有数字人的聊天会话
  digitalHumans: Record<string, DigitalHuman>
  // 当前活跃的数字人ID
  currentDigitalHumanId: string | null
  // 全局状态
  currentMessage: string
  isFailed: boolean
  // SSE相关状态
  sseConnected: boolean
  sseConnecting: boolean
  sseError: string | null
  // 超时定时器ID
  currentTimeoutId: ReturnType<typeof setTimeout> | null
  // 分页状态
  pagination: {
    page: number
    pageSize: number
    hasMore: boolean
    isLoadingMore: boolean
  }
  // 🔧 跟踪当前等待回复的会话信息
  pendingReply: {
    digitalHumanId: string | null
    sessionId: string | null
    timestamp: number
  } | null
  // 🔧 新增：专门管理等待回复的消息状态（用于未读消息管理）
  waitingReplies: Record<
    string,
    {
      digitalHumanId: string
      sessionId: string
      timestamp: number
      userMessageId: string // 用户发送的消息ID
    }
  >
  // 🔧 新增：全局自动翻译开关
  autoTranslate: boolean
  // 🔧 新增：初始化加载状态（用于首次加载聊天记录）
  isInitializing: boolean
  // 生图骨架屏状态（点击生成图片时置 See 推图后置 false）
  isGeneratingImage: boolean
  // 🔧 性能优化：标记哪些数字人数据已变更（脏数据标记）
  _dirtyDigitalHumans?: Set<string>
  // 🔧 新增：30秒自动重连定时器ID
  autoReconnectTimerId: NodeJS.Timeout | null
  // 🔧 新增：记录最后一次收到消息的时间
  lastMessageReceivedTime: number
  // 🔧 新增：补全定时器（3秒没新消息则触发补全）
  recoverTimer: ReturnType<typeof setTimeout> | null
  // 🔧 新增：记录最近一次 SSE 推送关联的用户消息 ID（msgId）
  lastSSEMsgId?: number | null
  // 🔧 新增：记录是否显示开场白弹窗
  isShowOpeningRemarksPopup: boolean
  // 🔧 新增：消息ID到消息位置的缓存映射（性能优化）
  _messageIdCache: Map<number, { digitalHumanId: string; sessionId: string; messageId: string }>
}

export const useChatStore = defineStore('chat', {
  state: (): ChatState => {
    // 轻量状态从 localStorage 读取，其余数据改由 IndexedDB 异步回填
    let ephemeral: any = {}
    if (typeof window !== 'undefined') {
      try {
        const e = localStorage.getItem('ai-chat-ephemeral')
        if (e) ephemeral = JSON.parse(e)
        // 一次性清理旧的本地存储键，避免占用空间
        localStorage.removeItem('ai-chat-store')
      } catch (_) { }
    }

    return {
      translateMessageLoading: 'done',
      latestSentMessageId: null,
      digitalHumans: {},
      currentDigitalHumanId: ephemeral.currentDigitalHumanId || null,
      isFailed: false,
      currentMessage: '',
      // 全局自动翻译开关（发送时附带到后端）
      autoTranslate: !!ephemeral.autoTranslate,
      sseConnected: false,
      sseConnecting: false,
      sseError: null,
      currentTimeoutId: null,
      pagination: {
        page: 1,
        pageSize: 20,
        hasMore: false,
        isLoadingMore: false
      },
      // 🔧 恢复轻量等待状态
      pendingReply: ephemeral.pendingReply || null,
      waitingReplies: ephemeral.waitingReplies || {},
      // 🔧 初始化加载状态
      isInitializing: false,
      // 生图骨架屏默认关闭
      isGeneratingImage: false,
      // 🔧 性能优化：脏数据标记
      _dirtyDigitalHumans: new Set<string>(),
      // 🔧 新增：30秒自动重连定时器ID
      autoReconnectTimerId: null as NodeJS.Timeout | null,
      // 🔧 新增：记录最后一次收到消息的时间
      lastMessageReceivedTime: 0,
      // 🔧 新增：补全定时器
      recoverTimer: null as ReturnType<typeof setTimeout> | null,
      // 🔧 新增：记录最近一次 SSE 推送的用户消息ID
      lastSSEMsgId: null,
      // 🔧 新增：记录是否显示开场白弹窗
      isShowOpeningRemarksPopup: false,
      // 🔧 新增：消息ID到消息位置的缓存映射（性能优化）
      _messageIdCache: new Map<number, { digitalHumanId: string; sessionId: string; messageId: string }>()
    }
  },

  getters: {
    // 当前数字人信息
    currentDigitalHuman: state => {
      if (!state.currentDigitalHumanId) return null
      return state.digitalHumans[state.currentDigitalHumanId] || null
    },

    // 当前数字人的所有会话
    currentSessions: state => {
      const current = state.digitalHumans[state.currentDigitalHumanId || '']
      return current?.sessions || []
    },

    // 当前活跃会话
    currentSession(): ChatSession | null {
      const current = this.currentDigitalHuman
      if (!current) return null

      // 1) 优先使用 currentSessionId
      if (current.currentSessionId) {
        let found = current.sessions.find(s => s.id === current.currentSessionId)
        if (found) return found
        // 🔧 关键：当 currentSessionId 存在但会话尚未加载（返回/刷新后），创建占位会话以承接流式输出
        const placeholder: ChatSession = {
          id: current.currentSessionId,
          name: 'Chat',
          messages: [],
          createdAt: Date.now(),
          updatedAt: Date.now(),
          avatar: current.avatar
        }
        current.sessions.push(placeholder)
        return placeholder
      }

      // 2) 兜底：当没有 currentSessionId 但已拉到会话，自动选择最近更新的会话
      if (current.sessions && current.sessions.length > 0) {
        const sorted = [...current.sessions].sort(
          (a: any, b: any) => (b.updatedAt || b.createdAt || 0) - (a.updatedAt || a.createdAt || 0)
        )
        return sorted[0] || null
      }

      return null
    },

    // 当前会话的消息
    currentMessages(): ChatMessage[] {
      const messages = this.currentSession?.messages || []

      // 🔧 去重：防止重复的消息 ID
      const uniqueMessages: ChatMessage[] = []
      const seenIds = new Set<string>()

      messages.forEach(msg => {
        if (msg.id && !seenIds.has(msg.id)) {
          seenIds.add(msg.id)
          uniqueMessages.push(msg)
        }
      })

      // 按时间戳排序，确保消息按时间顺序显示
      return uniqueMessages.sort((a, b) => a.timestamp - b.timestamp)
    },

    // 是否有消息
    hasMessages(): boolean {
      return this.currentMessages.length > 0
    },

    // 获取所有数字人列表
    digitalHumanList: state => {
      return Object.values(state.digitalHumans).map(dh => ({
        id: dh.id,
        name: dh.name,
        avatar: dh.avatar,
        sessionCount: dh.sessions.length,
        lastActivity: dh.sessions.length > 0 ? Math.max(...dh.sessions.map(s => s.updatedAt)) : 0
      }))
    },

    // 分页相关状态
    isLoadingMore: state => state.pagination.isLoadingMore,
    hasMoreMessages: state => state.pagination.hasMore,

    // 🔧 新增：获取当前数字人的加载和打字状态
    isLoading(): boolean {
      const current = this.currentDigitalHuman
      return current?.isLoading || false
    },
    isTyping(): boolean {
      const current = this.currentDigitalHuman
      return current?.isTyping || false
    },

    // 🔧 新增：获取总未读消息数量
    totalUnreadCount: state => {
      return Object.values(state.digitalHumans).reduce(
        (total, digitalHuman) => total + (digitalHuman.unreadCount || 0),
        0
      )
    },

    // 用户是否第一次使用应用聊天（检查所有数字人的所有会话）
    // isUserFirstChatEver(): boolean {
    //   // 先检查 localStorage 缓存标记（性能优化）
    //   const hasChattedBefore = localStorage.getItem('user_has_chatted_before')
    //   if (hasChattedBefore === 'true') {
    //     return false
    //   }

    //   // 如果缓存标记不存在，检查所有数字人的所有会话
    //   const allDigitalHumans = Object.values(this.digitalHumans)

    //   for (const digitalHuman of allDigitalHumans) {
    //     for (const session of digitalHuman.sessions || []) {
    //       const hasUserMessage = session.messages?.some(
    //         (msg: ChatMessage) => msg.type === MessageType.USER
    //       )
    //       if (hasUserMessage) {
    //         // 发现用户消息，设置缓存标记
    //         localStorage.setItem('user_has_chatted_before', 'true')
    //         return false
    //       }
    //     }
    //   }

    //   // 如果没有找到任何用户消息，返回 true
    //   return true
    // },

    // 🔧 新增：获取所有有未读消息的数字人
    digitalHumansWithUnread: state => {
      return Object.values(state.digitalHumans).filter(
        digitalHuman => (digitalHuman.unreadCount || 0) > 0
      )
    },

    // 🔧 新增：获取指定数字人的未读消息数量
    getUnreadCount: state => (digitalHumanId: string) => {
      const digitalHuman = state.digitalHumans[digitalHumanId]
      if (!digitalHuman) return 0

      // 🔧 兼容性处理：确保未读数量字段存在
      if (digitalHuman.unreadCount === undefined) {
        digitalHuman.unreadCount = 0
      }

      return digitalHuman.unreadCount
    }
  },

  actions: {
    /**
     * 🔧 新增：设置是否显示开场白弹窗
     */
    setIsShowOpeningRemarksPopup(isShow: boolean) {
      this.isShowOpeningRemarksPopup = isShow
    },
    /**
     * 🔧 新增：获取是否显示开场白弹窗
     */
    getIsShowOpeningRemarksPopup(): boolean {
      return this.isShowOpeningRemarksPopup
    },
    /**
     * 将 IndexedDB 中的译文覆盖到内存中的消息（按 message.id 匹配）
     */
    async overlayTranslationsFromIndexedDB(digitalHumanId: string) {
      try {
        await chatRecordsDB.init()
        const dh = this.digitalHumans[digitalHumanId]
        if (!dh) return
        for (const session of dh.sessions || []) {
          const localMsgs: any[] = await chatRecordsDB.getMessagesBySession(session.id)
          if (!Array.isArray(localMsgs) || localMsgs.length === 0) continue
          const byId = new Map(localMsgs.map((m: any) => [m.id, m]))
          session.messages = (session.messages || []).map((m: any) => {
            const lm = byId.get(m.id)
            if (lm && (lm.translated || lm.translatedContent || lm.translatedLang)) {
              return {
                ...m,
                translated: lm.translated === true,
                translatedContent: lm.translatedContent || m.translatedContent,
                translatedLang: lm.translatedLang || m.translatedLang
              }
            }
            return m
          })
        }
      } catch (_) { }
    },
    /**
     * 获取翻译函数（Store内部使用）
     */
    getTranslation(key: string, params?: Record<string, any>): string {
      const { t } = useStoreI18n()
      return t(key, params)
    },

    /**
     * 🔧 优化：清理指定数字人的加载和打字状态的公共方法
     */
    clearDigitalHumanStates(
      digitalHumanId: string,
      states: { loading?: boolean; typing?: boolean } = {}
    ) {
      const digitalHuman = this.digitalHumans[digitalHumanId]
      if (!digitalHuman) return

      let hasChanges = false

      if (states.loading !== undefined && digitalHuman.isLoading !== states.loading) {
        digitalHuman.isLoading = states.loading
        hasChanges = true
      }

      if (states.typing !== undefined && digitalHuman.isTyping !== states.typing) {
        digitalHuman.isTyping = states.typing
        hasChanges = true
      }

      if (hasChanges) {
        // 更新数字人状态
        this.saveToStorage()
      }
    },

    /**
     * 🔧 优化：批量清理多个数字人的状态
     */
    clearMultipleDigitalHumanStates(
      digitalHumanIds: string[],
      states: { loading?: boolean; typing?: boolean } = {}
    ) {
      digitalHumanIds.forEach(id => {
        this.clearDigitalHumanStates(id, states)
      })
    },

    /**
     * 🔧 优化：清理所有数字人的加载和打字状态
     */
    clearAllDigitalHumanStates(
      states: { loading?: boolean; typing?: boolean } = {
        loading: false,
        typing: false
      }
    ) {
      const digitalHumanIds = Object.keys(this.digitalHumans)
      this.clearMultipleDigitalHumanStates(digitalHumanIds, states)
    },

    /**
     * 🔧 新增：添加等待回复的消息
     */
    addWaitingReply(digitalHumanId: string, sessionId: string, userMessageId: string) {
      const waitingId = `${digitalHumanId}-${sessionId}-${userMessageId}`
      this.waitingReplies[waitingId] = {
        digitalHumanId,
        sessionId,
        timestamp: Date.now(),
        userMessageId
      }
      // 添加等待回复
      // 立即持久化，避免刷新丢失
      this.saveToStorage()
    },

    /**
     * 🔧 新增：移除等待回复的消息
     */
    removeWaitingReply(digitalHumanId: string, sessionId: string, userMessageId: string) {
      const waitingId = `${digitalHumanId}-${sessionId}-${userMessageId}`
      if (this.waitingReplies[waitingId]) {
        delete this.waitingReplies[waitingId]
        // 移除等待回复
        // 同步持久化
        this.saveToStorage()
      }
    },

    /**
     * 🔧 新增：检查数字人是否有等待回复的消息
     */
    hasWaitingReplies(digitalHumanId: string): boolean {
      return Object.values(this.waitingReplies).some(
        waiting => waiting.digitalHumanId === digitalHumanId
      )
    },

    /**
     * 🔧 新增：获取数字人的等待回复消息
     */
    getWaitingReplies(digitalHumanId: string) {
      return Object.entries(this.waitingReplies)
        .filter(([_, waiting]) => waiting.digitalHumanId === digitalHumanId)
        .map(([waitingId, waiting]) => ({ waitingId, ...waiting }))
    },

    /**
     * 🔧 新增：网络恢复后补全断网期间丢失的 AI 回复内容（仅检查当前数字人）
     */
    async recoverMissingAIReplies() {
      console.log('🔄 开始检查并补全断网期间丢失的消息...')

      // 🔧 只检查当前数字人
      const currentDigitalHuman = this.currentDigitalHuman
      if (!currentDigitalHuman) {
        console.log('⚠️ 没有当前数字人，跳过补全')
        return
      }

      const digitalHumanId = this.currentDigitalHumanId!
      const messagesToRecover: Array<{
        session: ChatSession
        userMessage: any
        userMessageIndex: number
        nextMessage: any
      }> = []

      // 扫描当前数字人的所有会话，查找需要补全的消息
      for (const session of currentDigitalHuman.sessions) {
        if (!session.messages || session.messages.length === 0) continue

        const lastMessage = session.messages[session.messages.length - 1]

        // 情况 1: 最后一条是用户消息，但没有 AI 回复
        if (lastMessage.type === 'user') {
          // 检查用户消息是否有 msgId（发送成功的标志）
          const hasMsgId = !!(lastMessage as any).msgId
          if (hasMsgId) {
            messagesToRecover.push({
              session,
              userMessage: lastMessage,
              userMessageIndex: session.messages.length - 1,
              nextMessage: null
            })
            console.log(
              `📝 发现需要补全的消息（无AI回复）: ${lastMessage.id}, msgId: ${(lastMessage as any).msgId}`
            )
          }
        }
        // 情况 2: 最后一条是 AI 消息，但处于 typing 状态（不完整）
        else if (lastMessage.type === 'ai' && lastMessage.isTyping) {
          const userMessageIndex = session.messages.length - 2
          if (userMessageIndex >= 0) {
            const userMessage = session.messages[userMessageIndex]
            if (userMessage && userMessage.type === 'user') {
              messagesToRecover.push({
                session,
                userMessage,
                userMessageIndex,
                nextMessage: lastMessage
              })
              console.log(`📝 发现需要补全的消息（typing状态）: ${userMessage.id}`)
            }
          }
        }
      }

      if (messagesToRecover.length === 0) {
        console.log('✅ 没有需要补全的消息')
        return
      }

      console.log(`📬 发现 ${messagesToRecover.length} 条需要补全的消息，开始补全...`)

      // 补全每条消息
      for (const item of messagesToRecover) {
        try {
          const { session, userMessage, userMessageIndex, nextMessage } = item

          console.log(`🔄 开始补全消息: ${userMessage.id}`)

          const msgIdToQuery = Number(localStorage.getItem('user_msg_id'))

          if (!msgIdToQuery) {
            console.warn(`⚠️ 没有找到 user_msg_id，跳过补全`)
            continue
          }

          // 🔧 轮询获取AI回复，直到获取到 content 有数据为止
          let retryCount = 0
          const MAX_RETRY_COUNT = 20 // 最多重试30次（总共60秒）
          let aiReply: string | null = null
          let msgIdValue: any = null

          while (retryCount < MAX_RETRY_COUNT) {

            retryCount++
            console.log(`🔄 第 ${retryCount} 次尝试获取AI回复...`)

            try {
              const response = await MessageAPI.getChatMsg({
                msgId: msgIdToQuery
              })

              const content = response?.data?.content
              const messageRole = response?.data?.role

              // 如果是用户消息，继续等待AI回复
              if (messageRole === 'user') {
                console.log(`⏳ 获取到用户消息，继续等待 AI 回复...`)
                await new Promise(resolve => setTimeout(resolve, 3000)) // 等待2秒后重试
                continue
              }

              // 如果获取到了AI回复且有内容
              if (content && messageRole !== 'user') {
                aiReply = content
                msgIdValue = response?.data?.msgId
                console.log(`✅ 成功获取到 AI 回复，长度: ${content.length}`)
                break // 获取成功，退出循环
              }

              // 如果没有内容，继续等待
              console.log(`⏳ 未获取到内容，2秒后重试...`)
              await new Promise(resolve => setTimeout(resolve, 2000)) // 等待2秒后重试
            } catch (error) {
              sendBurialPoint.longReplyFetchFailed()
              console.error(`❌ 第 ${retryCount} 次获取失败:`, error)
              await new Promise(resolve => setTimeout(resolve, 2000)) // 等待2秒后重试
            }
          }

          // 如果成功获取到AI回复，更新或创建消息
          if (aiReply) {
            // 如果已经有 AI 回复消息，更新它；否则创建新的
            if (nextMessage && nextMessage.type === 'ai') {
              // 更新现有消息
              nextMessage.content = aiReply
              nextMessage.isTyping = false
              nextMessage.status = 'received' as MessageStatus
              sendBurialPoint.networkUnstable()
              sendBurialPoint.longReplyFetchSuccess()
              // 更新消息 ID（如果后端返回了）
              if (msgIdValue) {
                ; (nextMessage as any).id = `${msgIdValue}`
                  ; (nextMessage as any).msgId = msgIdValue
              }

              console.log(`✅ 更新了现有 AI 消息`)
            } else {
              // 创建新的 AI 回复消息
              const aiMessage: ChatMessage = {
                id: msgIdValue ? `${msgIdValue}` : this.generateId(),
                timestamp: Date.now(),
                content: aiReply,
                type: 'ai' as MessageType,
                status: 'received' as MessageStatus,
                username: currentDigitalHuman.name || 'AI',
                avatar: currentDigitalHuman.avatar || '/assets/images/discover/dome.png',
                isTyping: false,
                msgId: msgIdValue
              }

              // 插入到用户消息后面
              session.messages.splice(userMessageIndex + 1, 0, aiMessage)

              console.log(`✅ 创建了新的 AI 消息`)
            }

            // 更新会话信息
            session.updatedAt = Date.now()
            session.lastMessage = aiReply

            // 清除当前数字人的加载和打字状态
            this.clearDigitalHumanStates(digitalHumanId, {
              loading: false,
              typing: false
            })

            // 保存到存储
            this.saveToStorage()

            console.log(`✅ 成功补全消息: ${userMessage.id}`)

            // 清除 localStorage 中的 user_msg_id
            localStorage.removeItem('user_msg_id')
          } else {
            sendBurialPoint.noMessagePulled()
            console.warn(`⚠️ 达到最大重试次数，仍未获取到 AI 回复`)
            this.clearAllDigitalHumanStates()
          }
        } catch (error) {
          console.error(`❌ 补全消息失败:`, error)
        }
      }

      console.log('✅ 断网消息补全完成')
    },

    /**
     * 🔧 优化：处理SSE断开连接的公共方法
     * ⚠️ 不要立即清除等待回复状态，因为可能是 App 切后台导致的临时断开
     */
    handleSSEDisconnect() {
      console.log('🔌 SSE 断开连接处理')

      // 清理所有数字人的加载和打字状态（但保留等待回复状态）
      // this.clearAllDigitalHumanStates({ loading: false, typing: false })

      // 🔧 关键修复：不要立即清除 pendingReply 和 waitingReplies
      // 因为 App 切到后台时 SSE 会断开，回到前台时重连
      // 如果立即清除，等待回复状态就丢失了

      // 只清除超时定时器（重连后会重新启动）
      this.clearTimeout()

      console.log('🔌 保留等待回复状态:', {
        pendingReply: this.pendingReply,
        waitingRepliesCount: Object.keys(this.waitingReplies).length
      })
    },

    /**
     * 🔧 处理内容：如果包含 </content>，只保留到 </content> 为止（包括标签），后面的文本不展示
     * @param content 原始内容
     * @returns 处理后的内容
     */
    processContentEnd(content: string): string {
      if (!content || typeof content !== 'string') return content

      // 查找 </content> 的位置
      const endContentIndex = content.indexOf('</content>')

      if (endContentIndex !== -1) {
        // 如果找到了 </content>，只保留到 </content> 结尾（包括标签本身）
        return content.substring(0, endContentIndex + '</content>'.length)
      }

      // 如果没有 </content>，正常返回全部内容
      return content
    },

    /**
     * 处理SSE消息
     */
    handleSSEMessage(data: any) {
      try {
        // 🔧 更新最后一次收到消息的时间
        this.lastMessageReceivedTime = Date.now()

        // 检查是否是聊天回复消息（文本/图片等）
        if (
          data.data?.content !== undefined &&
          data.data?.msgId &&
          data.data?.status !== undefined
        ) {
          const msgId = data.data.msgId // 消息ID，用于标识同一条回复（流式）
          const content = data.data.content // 本次的文本片段
          const status = data.data.status // 0=继续，1=结束
          const isComplete = status === 1 // status=1表示结束
          const isDone = content === '[DONE]' // [DONE]表示真正结束

          // 🔧 关键修复：优先使用后端返回的 roleId 来定位数字人
          const roleId = data.data.roleId ? String(data.data.roleId) : null
          const dialogueId = data.data.dialogueId ? String(data.data.dialogueId) : null

          // 🔧 新增：优先使用后端推送的真实记录ID（如 dialogueId / id）
          // 用于避免刷新或重新拉历史记录时出现一条回复有两条消息的情况
          // 注意：msgId 是用户消息的ID，而 id 是AI回复消息的唯一ID
          const serverRecordId = data.data.msgId as number | undefined

          // 🔧 记录最近一次 SSE 推送关联的用户消息 ID
          this.lastSSEMsgId = typeof msgId === 'number' ? msgId : Number(msgId) || null

          // 🔧 防抖补全：每次收到消息时，重置定时器，5秒后检查是否需要补全
          // 如果3秒内没有新消息，说明后台可能卡住了，触发补全
          this.recoverTimer && clearTimeout(this.recoverTimer)
          this.recoverTimer = setTimeout(() => {
            this.recoverMissingAIReplies().catch(() => { })
            this.recoverTimer = null  // 🔧 触发后清理变量
          }, 5000)

          // 如果是[DONE]消息，只需要结束状态，不处理内容
          if (isDone) {
            this.recoverTimer && clearTimeout(this.recoverTimer)
            // 收到[DONE]，结束流式消息

            // 🔧 性能优化：优先使用缓存查找消息，避免遍历
            let targetDigitalHumanId: string | null = null
            let targetAIMessage: any = null
            let targetSession: ChatSession | null = null

            // 策略 0: 使用缓存查找（最高性能）
            const cachedLocation = this._messageIdCache.get(msgId)
            if (cachedLocation) {
              const digitalHuman = this.digitalHumans[cachedLocation.digitalHumanId]
              if (digitalHuman) {
                const session = digitalHuman.sessions.find(s => s.id === cachedLocation.sessionId)
                if (session) {
                  const message = session.messages.find((m: any) => m.id === cachedLocation.messageId)
                  if (message && message.type === 'ai' && message.msgId === msgId) {
                    targetDigitalHumanId = cachedLocation.digitalHumanId
                    targetAIMessage = message
                    targetSession = session
                    // console.log('✅ [DONE] 通过缓存找到消息:', { msgId, sessionId: session.id })
                  } else {
                    // 缓存失效，清除缓存
                    this._messageIdCache.delete(msgId)
                  }
                }
              }
            }

            // 策略 1: 如果有 roleId，直接使用
            if (!targetDigitalHumanId && roleId && this.digitalHumans[roleId]) {
              targetDigitalHumanId = roleId
              const digitalHuman = this.digitalHumans[roleId]

              // 在该数字人的所有会话中查找对应的 AI 消息
              for (const session of digitalHuman.sessions) {
                const existingAIMessage = session.messages.find(
                  (m: any) => m.type === 'ai' && m.msgId === msgId
                )
                if (existingAIMessage) {
                  targetAIMessage = existingAIMessage
                  targetSession = session
                  break
                }
              }

              // console.log('✅ [DONE] 通过 roleId 定位到数字人:', roleId)
            }

            // 策略 2: Fallback - 通过 msgId 遍历查找（兼容旧逻辑）
            if (!targetDigitalHumanId) {
              for (const [digitalHumanId, digitalHuman] of Object.entries(this.digitalHumans)) {
                for (const session of digitalHuman.sessions) {
                  const existingAIMessage = session.messages.find(
                    (m: any) => m.type === 'ai' && m.msgId === msgId
                  )
                  if (existingAIMessage) {
                    targetDigitalHumanId = digitalHumanId
                    targetAIMessage = existingAIMessage
                    targetSession = session
                    break
                  }
                }
                if (targetDigitalHumanId) break
              }

              if (targetDigitalHumanId) {
                console.log('⚠️ [DONE] 通过 msgId 遍历找到数字人:', targetDigitalHumanId)
              }
            }

            if (targetDigitalHumanId) {
              const isTargetCurrentAI = targetDigitalHumanId === this.currentDigitalHumanId

              // 清除目标AI的状态
              this.clearDigitalHumanStates(targetDigitalHumanId, {
                loading: false,
                typing: false
              })
              // [DONE]清除目标AI状态

              // 🔧 最终检查：如果回复完成时用户不在该AI页面，确保标记为未读
              if (!isTargetCurrentAI) {
                // [DONE]时发现用户不在目标AI页面，确保标记为未读
                this.incrementUnreadCount(targetDigitalHumanId)
              }

              // 🔧 新增：如果开启了自动翻译，AI消息完成后自动翻译
              if (this.autoTranslate && targetAIMessage && targetSession) {
                // 检查消息是否已经有翻译内容，如果没有则自动翻译
                if (!targetAIMessage.translatedContent && targetAIMessage.content) {
                  // 异步执行翻译，不阻塞主流程
                  this.autoTranslateMessage(targetAIMessage, targetSession).catch(error => {
                    console.error('自动翻译失败:', error)
                  })
                }
              }
            }

            // 🔧 优化：只清除对应数字人的等待回复状态
            if (targetDigitalHumanId && targetSession) {
              // 移除该数字人的 waitingReply
              const waitingReplies = this.getWaitingReplies(targetDigitalHumanId)
              waitingReplies.forEach(waiting => {
                if (waiting.sessionId === targetSession.id) {
                  this.removeWaitingReply(
                    targetDigitalHumanId,
                    waiting.sessionId,
                    waiting.userMessageId
                  )
                  console.log('✅ [DONE] 移除 waitingReply:', {
                    roleId: targetDigitalHumanId,
                    sessionId: waiting.sessionId
                  })
                }
              })

              // 只有当 pendingReply 是该数字人时才清除
              if (Number(this.pendingReply?.digitalHumanId) === Number(targetDigitalHumanId)) {
                this.clearPendingReply()
                console.log('✅ [DONE] 清除 pendingReply:', targetDigitalHumanId)
              } else {
                console.log('⚠️ [DONE] 保留 pendingReply（不匹配）:', {
                  targetDigitalHumanId,
                  pendingDigitalHumanId: this.pendingReply?.digitalHumanId
                })
              }
            }

            // 清除超时定时器
            if (this.currentTimeoutId) {
              clearTimeout(this.currentTimeoutId)
              this.currentTimeoutId = null
            }

            // 🔧 已禁用：不再在收到 [DONE] 后强制重连
            // 改为依靠心跳超时检测（10秒未收到心跳回复才重连）
            console.log('✅ 收到 [DONE]，消息处理完成')

            return
          }

          // 🔧 智能路由：检查消息是否应该被处理（传入 roleId）
          const shouldProcess = this.shouldProcessMessage(msgId, roleId)
          if (!shouldProcess) {
            console.error('❌ 消息验证失败，跳过处理:', {
              msgId,
              roleId,
              pendingReply: this.pendingReply,
              currentDigitalHumanId: this.currentDigitalHumanId,
              waitingRepliesCount: Object.keys(this.waitingReplies || {}).length,
              hasExistingMessage: !!this.findMessageByMsgId(msgId)
            })

            // 🔧 修复：清除 loading 状态，避免用户看到永久的 loading
            // 优先使用 roleId，如果没有则使用 pendingReply
            const targetIdForClearStates = roleId || this.pendingReply?.digitalHumanId
            if (targetIdForClearStates) {
              this.clearDigitalHumanStates(targetIdForClearStates, {
                loading: false,
                typing: false
              })
              console.log('🧹 清除 loading 状态（验证失败）:', targetIdForClearStates)
            }
            return
          }

          // 🔧 关键修复：一旦收到第一段有效回复，就清除超时定时器
          // 这样超时提示只在"30/60秒内完全没有任何回复"时才会触发，
          // 而不会因为回复很长、30秒内没结束就误报超时
          this.clearTimeout()

          // ✅ 收到AI回复，清除所有无回复埋点定时器
          sendBurialPoint.clearNoReplyTrackingTimer()

          // 处理正常的文本片段（传入 roleId 和 dialogueId）
          this.updateOrCreateStreamMessage(
            msgId,
            content,
            isComplete,
            serverRecordId,
            roleId,
            dialogueId
          )

          // 如果本条 sse 消息里带有图片地址（和文本一起推），挂到对应的 AI 消息上
          const imageUrl =
            data.data?.imageUrl ||
            data.data?.image_url ||
            data.data?.imgUrl ||
            data.data?.img_url ||
            (Array.isArray(data.data?.images) ? data.data.images[0] : null)

          if (imageUrl) {
            const msg = this.findMessageByMsgId(msgId)
            if (msg) {
              ; (msg as any).generatedImageUrl = imageUrl
                ; (msg as any).image = imageUrl
              // 简单保存一次，确保刷新后还能看到图片
              this.saveToStorage()
            }
          }

          // 🔧 智能处理：根据目标AI和当前AI的关系来处理状态
          // 🔧 优先使用 roleId 定位数字人，提升并发对话准确性
          let typingDigitalHumanId: string | null = null

          // 策略 1: 优先使用 roleId（最可靠）
          if (roleId && this.digitalHumans[roleId]) {
            typingDigitalHumanId = roleId
          }

          // 策略 2: 使用缓存查找（性能优化）
          if (!typingDigitalHumanId) {
            const cachedLocation = this._messageIdCache.get(msgId)
            if (cachedLocation && this.digitalHumans[cachedLocation.digitalHumanId]) {
              typingDigitalHumanId = cachedLocation.digitalHumanId
              // console.log('✅ 通过缓存找到打字状态的数字人:', typingDigitalHumanId)
            } else {
              // Fallback - 通过 msgId 遍历查找（兼容旧逻辑）
              for (const [digitalHumanId, digitalHuman] of Object.entries(this.digitalHumans)) {
                for (const session of digitalHuman.sessions) {
                  const existingAIMessage = session.messages.find(
                    (m: any) => m.type === 'ai' && m.msgId === msgId
                  )
                  if (existingAIMessage) {
                    typingDigitalHumanId = digitalHumanId
                    console.log('⚠️ 通过 msgId 遍历找到打字状态的数字人:', digitalHumanId)
                    break
                  }
                }
                if (typingDigitalHumanId) break
              }
            }
          }

          if (typingDigitalHumanId && this.digitalHumans[typingDigitalHumanId]) {
            const isTargetCurrentAI = typingDigitalHumanId === this.currentDigitalHumanId

            const shouldShowTyping = !isComplete
            this.clearDigitalHumanStates(typingDigitalHumanId, {
              loading: false,
              typing: shouldShowTyping
            })

            // 🔧 智能未读标记：如果回复的不是当前活跃的AI，标记为未读
            if (!isTargetCurrentAI && isComplete) {
              this.incrementUnreadCount(typingDigitalHumanId)
            }
          }

          // 如果状态标识完成，清理状态
          if (isComplete) {
            // 注意：不立即清理，等待[DONE]消息
          }
          // 生图任务结果推送（仅图片，不含 content/msgId）
        } else if (data.data?.recordId && (data.data?.fileUrl)) {
          const recordId = Number(data.data.recordId)
          const imageUrl = data.data.fileUrl || data.data.thumbFileUrl

          if (imageUrl && recordId) {
            let updated = false
            let targetMsg: any = null

            // 🔧 性能优化：优先从当前数字人的当前会话中查找
            const currentDigitalHuman: DigitalHuman | null = this.currentDigitalHuman
            const currentSession = this.currentSession

            if (currentDigitalHuman && currentSession) {
              // 1. 优先查找当前会话
              targetMsg = currentSession.messages.find(
                (m: any) =>
                  m.type === 'ai' && (Number(m.msgId) === recordId || Number(m.id) === recordId)
              )
              if (!targetMsg) {
                // 2. 如果当前会话没有，查找当前数字人的其他会话
                for (const session of currentDigitalHuman.sessions) {
                  if (session.id === currentSession.id) continue // 跳过已查找的当前会话

                  targetMsg = session.messages.find(
                    (m: any) =>
                      m.type === 'ai' && (Number(m.msgId) === recordId || Number(m.id) === recordId)
                  )

                  if (targetMsg) {
                    console.log('✅ 在当前数字人的其他会话中找到生图消息:', recordId)
                    break
                  }
                }
              }
            }

            // 3. 如果还没找到，遍历所有数字人（兜底）
            if (!targetMsg) {
              console.log('⚠️ 在当前数字人中未找到，开始遍历所有数字人...')
              outerLoop: for (const digitalHuman of Object.values(this.digitalHumans)) {
                for (const session of digitalHuman.sessions) {
                  targetMsg = session.messages.find(
                    (m: any) =>
                      m.type === 'ai' && (Number(m.msgId) === recordId || Number(m.id) === recordId)
                  )

                  if (targetMsg) {
                    console.log('✅ 在其他数字人中找到生图消息:', recordId)
                    break outerLoop
                  }
                }
              }
            }

            // 更新消息
            if (targetMsg) {
              if (data.data.state === 30) {
                // ✅ 生图成功
                targetMsg.isSuccessful = true

                if (data.data.type === 1) {
                  targetMsg.image = imageUrl
                  targetMsg.generatedImageUrl = imageUrl
                } else if (data.data.type === 2) {
                  console.log(data.data, '------thumbFileUrlParent');
                  targetMsg.image = data.data.thumbFileUrlParent
                  targetMsg.video = imageUrl
                  targetMsg.coverImage = data.data.thumbFileUrlParent
                }
                // 🔧 优化：先不设置 isGeneratingImage = false，等图片真正加载完成后再设置
                // 这样可以避免骨架屏消失后出现空白
                // msg.isGeneratingImage = false  // 注释掉，由组件在图片加载完成时设置
                // 🔧 标记图片生成完成，用于设置进度为100%
                ; (targetMsg as any).imageProgress = 100
                // 生图完成，清除对应数字人的生图任务标记
                currentDigitalHuman!.hasActiveImageTask = false
                updated = true
                console.log('✅ 生图成功:', { recordId, imageUrl })
              } else if (data.data.state === 2) {
                // ❌ 生图失败
                targetMsg.isSuccessful = false
                targetMsg.image = null
                targetMsg.generatedImageUrl = null
                  ; (targetMsg as any).imageProgress = 0
                targetMsg.hasGeneratedImage = false
                // 生图失败也需要清除生图任务标记
                currentDigitalHuman!.hasActiveImageTask = false
                updated = true
                console.log('❌ 生图失败:', { recordId, state: data.data.state })
              }
            }

            if (updated) {
              // 🔧 性能优化：标记相关数字人为脏数据
              if (this.currentDigitalHumanId) {
                this.markDigitalHumanDirty(this.currentDigitalHumanId)
              }
              this.saveToStorage()
            } else {
              console.warn('⚠️ 收到生图结果但未找到对应消息', { recordId, imageUrl })
            }
          }
        } else if (data.type === 'typing_status') {
          // 🔧 当前忽略服务器的全局 typing_status，避免错误影响其他数字人的状态
          // 实际的打字状态由上面的流式消息逻辑（msgId + waitingReplies）精确控制
        } else if (data.type === 'ping' || data.type === 'pong') {
          // 心跳消息，更新连接状态
          // 收到心跳消息
          // 可以在这里更新最后心跳时间等状态
        } else {
          // 未知SSE消息类型
        }
      } catch (error) {
        // SSE消息处理错误
        // 🔧 优化：出错时清理状态，但保留pendingReply以允许后续消息继续
        const targetDigitalHumanId = this.pendingReply?.digitalHumanId
        if (targetDigitalHumanId) {
          this.clearDigitalHumanStates(targetDigitalHumanId, {
            loading: false,
            typing: false
          })
        }
        // 注意：不清除pendingReply，以免影响后续的流式消息
      }
    },

    /**
     * 🔧 辅助方法：根据 msgId 查找消息（使用缓存优化性能）
     */
    findMessageByMsgId(msgId: number): ChatMessage | null {
      // 🔧 性能优化：优先使用缓存查找
      const cachedLocation = this._messageIdCache.get(msgId)
      if (cachedLocation) {
        const digitalHuman = this.digitalHumans[cachedLocation.digitalHumanId]
        if (digitalHuman) {
          const session = digitalHuman.sessions.find(s => s.id === cachedLocation.sessionId)
          if (session) {
            const message = session.messages.find((m: any) => m.id === cachedLocation.messageId)
            if (message && message.type === 'ai' && message.msgId === msgId) {
              return message
            } else {
              // 缓存失效，清除缓存
              this._messageIdCache.delete(msgId)
            }
          }
        }
      }

      // 🔧 Fallback：遍历查找（兼容旧逻辑）
      for (const [digitalHumanId, digitalHuman] of Object.entries(this.digitalHumans)) {
        for (const session of digitalHuman.sessions) {
          const message = session.messages.find((m: any) => m.type === 'ai' && m.msgId === msgId)
          if (message) {
            // 🔧 找到后更新缓存
            this._messageIdCache.set(msgId, {
              digitalHumanId,
              sessionId: session.id,
              messageId: message.id
            })
            return message
          }
        }
      }
      return null
    },

    /**
     * 🔧 重构：智能检查消息是否应该被处理
     * 允许消息路由到正确的AI，即使用户已经切换了AI
     */
    shouldProcessMessage(msgId: number, roleId?: string | null): boolean {
      // 🔧 策略 1: 如果有 roleId，优先使用（最可靠）
      if (roleId) {
        // 检查该数字人是否存在
        if (this.digitalHumans[roleId]) {
          // console.log('✅ 通过 roleId 验证消息，允许处理:', { roleId, msgId })
          return true
        } else {
          // 数字人不存在，但有 roleId，也放行（后续会创建占位数字人）
          // console.log('⚠️ roleId 对应的数字人不存在，但仍允许处理（将创建占位）:', { roleId, msgId })
          return true
        }
      }

      // 🔧 策略 2: 检查所有会话中是否已存在该msgId的AI消息
      for (const digitalHuman of Object.values(this.digitalHumans)) {
        for (const session of digitalHuman.sessions) {
          const existingAIMessage = session.messages.find(
            (message: any) => message.type === 'ai' && message.msgId === msgId
          )
          if (existingAIMessage) {
            console.log('✅ 找到已存在的消息，允许处理:', { msgId })
            return true
          }
        }
      }

      // 🔧 策略 3: 如果没有待回复状态，检查是否存在等待队列
      if (!this.pendingReply) {
        const waitingEntries = Object.values(this.waitingReplies || {})
        const hasValidWaiting = waitingEntries.some(
          (w: any) => Date.now() - (w.timestamp || 0) < MESSAGE_VALIDATION_WINDOW_MS
        )
        if (!hasValidWaiting) {
          // 没有待回复状态且没有有效的等待队列
          console.log('❌ 没有待回复状态且没有等待队列，拒绝处理:', { msgId })
          return false
        }
        // 存在有效等待队列则放行
        console.log('✅ 存在等待队列，允许处理:', { msgId })
        return true
      }

      // 🔧 策略 4: 检查 pendingReply（兼容旧逻辑）
      const isValidPendingReply = this.pendingReply.digitalHumanId !== null

      // 检查时间窗口（10分钟内的消息认为是有效的）
      const isWithinTimeWindow =
        Date.now() - this.pendingReply.timestamp < MESSAGE_VALIDATION_WINDOW_MS

      if (isValidPendingReply && isWithinTimeWindow) {
        console.log('✅ 通过 pendingReply 验证，允许处理:', {
          msgId,
          pendingDigitalHumanId: this.pendingReply.digitalHumanId
        })
        return true
      }

      console.log('❌ 所有验证策略都失败，拒绝处理:', { msgId, roleId })
      return false
    },

    /**
     * 更新或创建流式消息 - 基于msgId和status
     * 🔧 优化：优先使用 roleId 定位数字人，提升并发对话准确性
     */
    updateOrCreateStreamMessage(
      msgId: number,
      content: string,
      isComplete: boolean,
      serverRecordId?: number,
      roleId?: string | null,
      dialogueId?: string | null
    ) {
      
      // 🔧 性能优化：优先使用缓存查找已存在的消息
      let targetSession: ChatSession | null = null
      let targetDigitalHumanId: string | null = null
      let aiMessage: ChatMessage | null = null

      // 策略 0: 使用缓存查找（最高性能）
      const cachedLocation = this._messageIdCache.get(msgId)
      if (cachedLocation) {
        const digitalHuman = this.digitalHumans[cachedLocation.digitalHumanId]
        if (digitalHuman) {
          const session = digitalHuman.sessions.find(s => s.id === cachedLocation.sessionId)
          if (session) {
            const message = session.messages.find((m: any) => m.id === cachedLocation.messageId)
            if (message && message.type === 'ai' && message.msgId === msgId) {
              aiMessage = message
              targetSession = session
              targetDigitalHumanId = cachedLocation.digitalHumanId
              // console.log('✅ 通过缓存找到消息:', { msgId, sessionId: session.id })
            } else {
              // 缓存失效，清除缓存
              this._messageIdCache.delete(msgId)
            }
          }
        }
      }

      // 策略 1: 如果有 roleId，优先在该数字人中查找
      if (roleId && this.digitalHumans[roleId]) {
        targetDigitalHumanId = roleId
        const digitalHuman = this.digitalHumans[roleId]

        // 在该数字人的所有会话中查找对应的 AI 消息
        for (const session of digitalHuman.sessions) {
          const existingMessage = session.messages.find(
            (m: any) => m.type === 'ai' && m.msgId === msgId
          )
          if (existingMessage) {
            targetSession = session
            aiMessage = existingMessage
            // console.log('✅ 通过 roleId 找到已存在的消息:', { roleId, msgId, sessionId: session.id })
            break
          }
        }

        // 如果在该数字人中没找到消息，但找到了数字人，记录下来（稍后创建新消息）
        if (!aiMessage) {
          // console.log('📝 通过 roleId 定位到数字人，但未找到已存在的消息，稍后创建新消息:', roleId)
        }
      }

      // 策略 2: Fallback - 通过 msgId 遍历所有数字人查找（兼容旧逻辑）
      if (!aiMessage && !roleId) {
        for (const [digitalHumanId, digitalHuman] of Object.entries(this.digitalHumans)) {
          for (const session of digitalHuman.sessions) {
            const existingMessage = session.messages.find(
              (m: any) => m.type === 'ai' && m.msgId === msgId
            )
            if (existingMessage) {
              targetSession = session
              targetDigitalHumanId = digitalHumanId
              aiMessage = existingMessage
              console.log('⚠️ 通过 msgId 遍历找到消息:', { digitalHumanId, msgId })
              break
            }
          }
          if (targetSession) break
        }
      }

      // 🔧 关键修复：如果没找到已存在的消息，则在原来发送消息的数字人会话中创建新消息
      if (!aiMessage) {
        // 优先尝试在待回复的目标会话中创建消息
        let targetSessionForNewMessage: ChatSession | null = null
        let targetDigitalHumanIdForNewMessage: string | null = null
        let foundWaitingReply = null // 提升作用域

        // 🔧 策略 1: 如果有 roleId，直接使用该数字人（最高优先级）
        if (roleId && this.digitalHumans[roleId]) {
          targetDigitalHumanIdForNewMessage = roleId
          const digitalHuman = this.digitalHumans[roleId]

          // 优先使用 dialogueId 查找会话
          if (dialogueId) {
            targetSessionForNewMessage =
              digitalHuman.sessions.find(s => s.id === dialogueId) || null
            if (targetSessionForNewMessage) {
              // console.log('✅ 通过 roleId + dialogueId 找到会话:', { roleId, dialogueId })
            }
          }

          // 如果没有 dialogueId 或找不到对应会话，使用最近的会话或创建新会话
          if (!targetSessionForNewMessage && digitalHuman.sessions.length > 0) {
            const sortedSessions = [...digitalHuman.sessions].sort(
              (a, b) => (b.updatedAt || b.createdAt || 0) - (a.updatedAt || a.createdAt || 0)
            )
            targetSessionForNewMessage = sortedSessions[0]
            // console.log('✅ 通过 roleId 使用最近会话:', { roleId, sessionId: targetSessionForNewMessage.id })
          }

          // 如果该数字人没有会话，创建一个新会话
          if (!targetSessionForNewMessage) {
            const newSessionId =
              dialogueId || `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`
            targetSessionForNewMessage = {
              id: newSessionId,
              name: 'Chat',
              messages: [],
              createdAt: Date.now(),
              updatedAt: Date.now(),
              avatar: digitalHuman.avatar,
              lastMessage: ''
            } as ChatSession
            digitalHuman.sessions.push(targetSessionForNewMessage)
            // console.log('📝 通过 roleId 创建新会话:', { roleId, sessionId: newSessionId })
          }
        }

        // 🔧 策略 2: Fallback - 检查 waitingReplies 中是否有匹配的记录
        if (!targetSessionForNewMessage) {
          const waitingEntries = Object.entries(this.waitingReplies)

          // console.log('🔍 Fallback: 查找目标会话 - waitingReplies:', {
          //   msgId,
          //   waitingEntriesCount: waitingEntries.length,
          //   pendingReply: this.pendingReply
          // })

          for (const [waitingId, waiting] of waitingEntries) {
            if (waiting.digitalHumanId && waiting.sessionId) {
              const digitalHuman = this.digitalHumans[waiting.digitalHumanId]
              if (digitalHuman) {
                const session = digitalHuman.sessions.find(s => s.id === waiting.sessionId)
                if (session) {
                  // 找到有效的等待回复记录
                  foundWaitingReply = {
                    digitalHumanId: waiting.digitalHumanId,
                    session: session,
                    waitingId: waitingId
                  }
                  console.log('✅ 通过 waitingReplies 找到会话:', {
                    digitalHumanId: waiting.digitalHumanId,
                    sessionId: waiting.sessionId
                  })
                  break
                }
              }
            }
          }

          if (foundWaitingReply) {
            targetSessionForNewMessage = foundWaitingReply.session
            targetDigitalHumanIdForNewMessage = foundWaitingReply.digitalHumanId
          }
        }

        // 🔧 策略 3: Fallback - 使用 pendingReply 精确定位
        if (
          !targetSessionForNewMessage &&
          this.pendingReply &&
          this.pendingReply.digitalHumanId &&
          this.pendingReply.sessionId
        ) {
          // 回退到原来的逻辑：用 pendingReply 精确定位
          console.log('🔍 Fallback: 通过 pendingReply 查找会话:', {
            digitalHumanId: this.pendingReply.digitalHumanId,
            sessionId: this.pendingReply.sessionId
          })

          const originalDigitalHuman = this.digitalHumans[this.pendingReply.digitalHumanId]
          if (originalDigitalHuman) {
            targetSessionForNewMessage =
              originalDigitalHuman.sessions.find(s => s.id === this.pendingReply!.sessionId) || null
            targetDigitalHumanIdForNewMessage = this.pendingReply.digitalHumanId

            // 刷新后会话可能尚未从后端加载；此时创建占位会话，避免丢片段
            if (!targetSessionForNewMessage) {
              console.log('⚠️ 会话不存在，创建占位会话:', {
                digitalHumanId: this.pendingReply.digitalHumanId,
                sessionId: this.pendingReply.sessionId
              })

              targetSessionForNewMessage = {
                id: this.pendingReply.sessionId,
                name: 'Chat',
                messages: [],
                createdAt: Date.now(),
                updatedAt: Date.now(),
                avatar: originalDigitalHuman.avatar,
                lastMessage: ''
              } as ChatSession
              originalDigitalHuman.sessions.push(targetSessionForNewMessage)
              // 如果当前会话为空，也同步指向它
              if (!originalDigitalHuman.currentSessionId) {
                originalDigitalHuman.currentSessionId = this.pendingReply.sessionId
              }
              this.saveToStorage()
            } else {
              console.log('✅ 通过 pendingReply 找到会话')
            }
          }
        }

        // 🔧 新增：Fallback 逻辑 - 如果通过 sessionId 找不到，尝试通过数字人 ID 找最近的会话
        if (!targetSessionForNewMessage && this.pendingReply?.digitalHumanId) {
          console.log('🔍 Fallback: 通过数字人 ID 查找最近的会话:', {
            digitalHumanId: this.pendingReply.digitalHumanId
          })

          const digitalHuman = this.digitalHumans[this.pendingReply.digitalHumanId]
          if (digitalHuman && digitalHuman.sessions.length > 0) {
            // 找到最近更新的会话（可能是用户刚发送消息的会话）
            const sortedSessions = [...digitalHuman.sessions].sort(
              (a, b) => (b.updatedAt || b.createdAt || 0) - (a.updatedAt || a.createdAt || 0)
            )
            const latestSession = sortedSessions[0]

            if (latestSession) {
              targetSessionForNewMessage = latestSession
              targetDigitalHumanIdForNewMessage = this.pendingReply.digitalHumanId

              console.log('✅ Fallback 成功: 使用最近的会话:', {
                sessionId: latestSession.id,
                digitalHumanId: this.pendingReply.digitalHumanId
              })

              // 更新 pendingReply 的 sessionId 为实际会话 ID
              this.pendingReply.sessionId = latestSession.id
              this.saveToStorage()
            }
          }
        }

        // 🔧 修复：如果找不到原来的会话，不要fallback到当前会话，应该跳过这个消息
        if (!targetSessionForNewMessage) {
          // 再次尝试：如果连数字人对象都没有，创建占位数字人与会话
          if (
            this.pendingReply &&
            this.pendingReply.digitalHumanId &&
            this.pendingReply.sessionId &&
            !this.digitalHumans[this.pendingReply.digitalHumanId]
          ) {
            this.digitalHumans[this.pendingReply.digitalHumanId] = {
              id: this.pendingReply.digitalHumanId,
              name: 'AI Assistant',
              avatar: '/assets/images/discover/dome.png',
              sessions: [],
              currentSessionId: this.pendingReply.sessionId,
              lastSyncTime: 0,
              isFromAPI: false,
              unreadCount: 0,
              lastActiveTime: Date.now(),
              isLoading: false,
              isTyping: false
            }
            targetSessionForNewMessage = {
              id: this.pendingReply.sessionId,
              name: 'Chat',
              messages: [],
              createdAt: Date.now(),
              updatedAt: Date.now(),
              lastMessage: ''
            } as ChatSession
            this.digitalHumans[this.pendingReply.digitalHumanId].sessions.push(
              targetSessionForNewMessage
            )
            targetDigitalHumanIdForNewMessage = this.pendingReply.digitalHumanId
            this.saveToStorage()
          }

          if (!targetSessionForNewMessage) {
            // 无法找到消息对应的原始会话，跳过处理
            console.warn('❌ 无法找到消息对应的会话，跳过处理', {
              msgId,
              pendingReply: this.pendingReply,
              waitingRepliesCount: Object.keys(this.waitingReplies || {}).length
            })

            // 🔧 修复：清除 loading 状态，避免用户看到永久的 loading
            if (this.pendingReply?.digitalHumanId) {
              this.clearDigitalHumanStates(this.pendingReply.digitalHumanId, {
                loading: false,
                typing: false
              })
            }
            return // 直接返回，不处理这个消息
          }
        }

        if (!targetSessionForNewMessage) {
          console.warn('❌ 最终未找到目标会话，清除状态', { msgId })
          // 🔧 修复：清除 loading 状态
          if (this.pendingReply?.digitalHumanId) {
            this.clearDigitalHumanStates(this.pendingReply.digitalHumanId, {
              loading: false,
              typing: false
            })
          }
          return
        }

        // 获取目标数字人信息
        const targetDigitalHuman = targetDigitalHumanIdForNewMessage
          ? this.digitalHumans[targetDigitalHumanIdForNewMessage]
          : null

        // 尝试复用已有的"打字中但还没有 msgId"的AI气泡，避免恢复后分成两段
        if (targetSessionForNewMessage) {
          const existingTyping = [...(targetSessionForNewMessage.messages || [])]
            .reverse()
            .find((m: any) => m.type === 'ai' && m.isTyping && !m.msgId)
          if (existingTyping) {
            existingTyping.msgId = msgId

            // 🔧 直接用新内容替换（后端已改为一次性返回完整内容）
            existingTyping.content = this.processContentEnd(content || '')
            existingTyping.isTyping = !isComplete

            // 🔧 设置全局翻译标记（如果消息还没有这个字段）
            if ((existingTyping as any).globalTranslation === undefined) {
              const isGlobalTranslationEnabled =
                targetDigitalHumanIdForNewMessage &&
                  this.digitalHumans[targetDigitalHumanIdForNewMessage]
                  ? this.digitalHumans[targetDigitalHumanIdForNewMessage]
                    .isGlobalTranslationEnabled || false
                  : false
                ; (existingTyping as any).globalTranslation = isGlobalTranslationEnabled
            }

            // 若全局自动翻译开启，仅在未被用户显式关闭时标记翻译
            if (this.autoTranslate && (existingTyping as any).translated !== false) {
              try {
                const { shortLang } = useLanguage()
                  ; (existingTyping as any).translated = true
                if (!(existingTyping as any).translatedLang) {
                  ; (existingTyping as any).translatedLang = shortLang.value
                }
              } catch (_) {
                ; (existingTyping as any).translated = true
              }
            }

            if (isComplete) {
              existingTyping.status = 'received' as MessageStatus

              // 完成时清理等待状态
              if (targetDigitalHumanIdForNewMessage) {
                const waitingList = this.getWaitingReplies(targetDigitalHumanIdForNewMessage)
                const matching = waitingList.find(
                  w => w.sessionId === targetSessionForNewMessage.id
                )
                if (matching) {
                  this.removeWaitingReply(
                    targetDigitalHumanIdForNewMessage,
                    targetSessionForNewMessage.id,
                    matching.userMessageId
                  )
                }
              }
            }

            targetSessionForNewMessage.updatedAt = Date.now()
            targetSessionForNewMessage.lastMessage = existingTyping.content
            this.saveToStorage()

            // 复用完成，直接返回
            return
          }
        }

        // 首次收到该msgId的消息，创建新的AI气泡（跳过空片段）
        // 🔧 关键：使用后端的真实记录ID（serverRecordId）作为本地消息ID
        // 这样后续从历史接口拉回来的同一条消息（按 id）就能对上，不会重复一条回复两条消息
        // 🔧 处理内容：如果包含 </content>，只保留到 </content> 为止
        const cleanContent = this.processContentEnd(content || '')

        // 🔧 获取目标数字人的全局翻译状态
        const isGlobalTranslationEnabled =
          targetDigitalHumanIdForNewMessage && this.digitalHumans[targetDigitalHumanIdForNewMessage]
            ? this.digitalHumans[targetDigitalHumanIdForNewMessage].isGlobalTranslationEnabled ||
            false
            : false

        const newMessage: ChatMessage = {
          id: String(serverRecordId ?? msgId),
          timestamp: Date.now(),
          content: cleanContent,
          type: 'ai' as MessageType,
          status: 'received' as MessageStatus,
          username: targetDigitalHuman?.name || 'AI Assistant',
          avatar: targetDigitalHuman?.avatar || '/assets/images/discover/dome.png',
          isTyping: !isComplete, // 如果未完成则显示打字状态
          msgId: msgId, // 🔧 关键：设置msgId用于后续查找
          isLoadingTranslate: false,
          // 🔧 初始化生图相关字段
          isSuccessful: undefined, // 未生图
          hasGeneratedImage: false,
          isGeneratingImage: false,
          // 🔧 设置全局翻译标记
          globalTranslation: isGlobalTranslationEnabled
        }

        // 🔧 若开启全局自动翻译，仅在未被用户显式关闭时标记为已翻译
        if (this.autoTranslate && (newMessage as any).translated !== false) {
          try {
            const { shortLang } = useLanguage()
              ; (newMessage as any).translated = true
              ; (newMessage as any).translatedLang = shortLang.value
          } catch (_) {
            ; (newMessage as any).translated = true
          }
        }

        // 将消息添加到目标会话中
        targetSessionForNewMessage.messages.push(newMessage)
        targetSessionForNewMessage.updatedAt = Date.now()
        targetSessionForNewMessage.lastMessage = cleanContent

        aiMessage = newMessage
        targetSession = targetSessionForNewMessage
        targetDigitalHumanId = targetDigitalHumanIdForNewMessage

        // 🔧 性能优化：创建新消息时更新缓存
        if (targetDigitalHumanIdForNewMessage) {
          this._messageIdCache.set(msgId, {
            digitalHumanId: targetDigitalHumanIdForNewMessage,
            sessionId: targetSessionForNewMessage.id,
            messageId: newMessage.id
          })
        }

        // 🔧 新增：如果是通过waitingReplies找到的会话，清理对应的等待记录
        if (foundWaitingReply) {
          delete this.waitingReplies[foundWaitingReply.waitingId]
        }

        // 🔧 性能优化：标记目标数字人数据已变更
        if (targetDigitalHumanIdForNewMessage) {
          this.markDigitalHumanDirty(targetDigitalHumanIdForNewMessage)
        }

        this.saveToStorage()
      } else {
        // 🔧 更新现有消息 - 直接用新内容替换（后端已改为一次性返回完整内容）
        aiMessage.content = this.processContentEnd(content)
        aiMessage.isTyping = !isComplete

        // 🔧 若开启全局自动翻译，仅在未被用户显式关闭时保持 translated=true
        if (this.autoTranslate && aiMessage && (aiMessage as any).translated !== false) {
          try {
            const { shortLang } = useLanguage()
              ; (aiMessage as any).translated = true
            if (!(aiMessage as any).translatedLang) {
              ; (aiMessage as any).translatedLang = shortLang.value
            }
          } catch (_) {
            ; (aiMessage as any).translated = true
          }
        }
        if (isComplete) {
          aiMessage.status = 'received' as MessageStatus

          // 🔧 新逻辑：消息完成时处理等待回复状态
          if (targetDigitalHumanId && targetSession) {
            // 找到对应的等待回复记录并移除
            const waitingReplies = this.getWaitingReplies(targetDigitalHumanId)
            const matchingWaiting = waitingReplies.find(
              waiting => waiting.sessionId === targetSession?.id
            )

            if (matchingWaiting) {
              this.removeWaitingReply(
                targetDigitalHumanId,
                targetSession.id,
                matchingWaiting.userMessageId
              )

              // 🔧 新增：如果开启了自动翻译（isAllTranslate），消息完成时自动翻译
              if (this.autoTranslate && aiMessage && targetSession) {
                // 检查消息是否已经有翻译内容，如果没有则自动翻译
                if (!aiMessage.translatedContent && aiMessage.content) {
                  // 异步执行翻译，不阻塞主流程
                  this.autoTranslateMessage(aiMessage, targetSession).catch((error: any) => {
                    console.error('自动翻译失败:', error)
                  })
                }
              }

              // 检查用户是否在目标会话中
              const isUserInTargetSession =
                this.currentDigitalHumanId === targetDigitalHumanId &&
                this.currentSession?.id === targetSession.id

              if (isUserInTargetSession) {
                // 用户在目标会话中，清除未读状态
                this.clearUnreadCount(targetDigitalHumanId)
              } else {
                // 用户不在目标会话中，这个未读状态已经在切换时设置了
              }
            }
          }
        }

        // 🔧 性能优化：标记目标数字人数据已变更
        if (targetDigitalHumanId) {
          this.markDigitalHumanDirty(targetDigitalHumanId)
        }

        // 保存到存储
        this.saveToStorage()
      }
    },

    /**
     * 保存数据到localStorage
     */
    /**
     * 🔧 优化：立即保存轻量状态到 localStorage
     * 用于需要立即持久化的场景（如退出登录前）
     */
    saveLightweightState() {
      try {
        const ephemeral = {
          currentDigitalHumanId: this.currentDigitalHumanId,
          autoTranslate: this.autoTranslate,
          pendingReply: this.pendingReply,
          waitingReplies: this.waitingReplies,
          isGeneratingImage: this.isGeneratingImage
        }
        localStorage.setItem('ai-chat-ephemeral', JSON.stringify(ephemeral))
      } catch (error) {
        console.error('保存轻量状态失败:', error)
      }
    },

    /**
     * 🔧 优化：立即保存所有数据（不防抖）
     * 用于页面卸载、退出登录等需要立即持久化的场景
     */
    async saveImmediately() {
      // 清除防抖定时器
      if (saveDebounceTimer) {
        clearTimeout(saveDebounceTimer)
        saveDebounceTimer = null
      }

      // 立即保存
      this.saveLightweightState()

      try {
        await chatRecordsDB.init()

        // 批量收集所有需要写入的数据
        const digitalHumansToSave: ChatRecordDigitalHuman[] = []
        const sessionsToSave: ChatRecordSession[] = []
        const messagesToSave: ChatRecordMessage[] = []

        for (const [dhId, dh] of Object.entries(this.digitalHumans)) {
          digitalHumansToSave.push({
            id: dhId,
            name: dh.name,
            avatar: dh.avatar,
            currentSessionId: dh.currentSessionId,
            lastActiveTime: dh.lastActiveTime || Date.now(),
            createdAt: Date.now(),
            updatedAt: Date.now()
          })

          for (const session of dh.sessions) {
            sessionsToSave.push({
              id: session.id,
              digitalHumanId: dhId,
              name: session.name || 'Chat',
              createdAt: session.createdAt || Date.now(),
              updatedAt: session.updatedAt || Date.now(),
              avatar: session.avatar,
              lastMessage: session.lastMessage
            })

            const messages: ChatRecordMessage[] = (session.messages || []).map((m: any) => ({
              id: m.id,
              sessionId: session.id,
              content: m.content || '',
              translatedContent: m.translatedContent,
              translatedLang: m.translatedLang,
              translated: m.translated,
              globalTranslation: m.globalTranslation, // 🔧 新增：全局翻译标记
              type: m.type || 'user',
              status: m.status || 'sent',
              timestamp: m.timestamp || Date.now(),
              username: m.username,
              avatar: m.avatar,
              isTyping: m.isTyping,
              msgId: m.msgId,
              image: m.image,
              video: m.video,
              coverImage: m.coverImage,
              hasGeneratedImage: m.hasGeneratedImage,
              isGeneratingImage: m.isGeneratingImage,
              isSuccessful: m.isSuccessful, // 🔧 修复：保存生图成功状态
              imageProgress: m.imageProgress
            }))
            messagesToSave.push(...messages)
          }
        }

        // 批量写入
        await Promise.all([
          chatRecordsDB.saveDigitalHumans(digitalHumansToSave),
          chatRecordsDB.saveSessions(sessionsToSave),
          chatRecordsDB.saveMessages(messagesToSave)
        ])
      } catch (e) {
        console.error('立即保存失败:', e)
      }
    },

    /**
     * 🔧 性能优化：标记数字人数据已变更
     */
    markDigitalHumanDirty(digitalHumanId: string) {
      if (!this._dirtyDigitalHumans) {
        this._dirtyDigitalHumans = new Set()
      }
      this._dirtyDigitalHumans.add(digitalHumanId)
    },

    /**
     * 🔧 优化：使用防抖的保存方法（增量保存 + requestIdleCallback）
     * 避免频繁调用导致性能问题
     */
    async saveToStorage() {
      // 立即保存轻量状态到 localStorage（不防抖）
      this.saveLightweightState()

      // 标记当前活跃的数字人为脏数据
      if (this.currentDigitalHumanId) {
        this.markDigitalHumanDirty(this.currentDigitalHumanId)
      }

      // 清除之前的防抖定时器
      if (saveDebounceTimer) {
        clearTimeout(saveDebounceTimer)
      }

      // 设置新的防抖定时器，延迟保存到 IndexedDB
      saveDebounceTimer = setTimeout(() => {
        // 🔧 使用 requestIdleCallback 在浏览器空闲时保存
        const idleCallback = async () => {
          try {
            await chatRecordsDB.init()

            // 🔧 增量保存：只保存标记为脏的数字人数据
            const dirtyIds = Array.from(this._dirtyDigitalHumans || [])

            if (dirtyIds.length === 0) {
              // 没有脏数据，跳过
              return
            }

            // 优先保存当前活跃的数字人
            const sortedIds = dirtyIds.sort((a, b) => {
              if (a === this.currentDigitalHumanId) return -1
              if (b === this.currentDigitalHumanId) return 1
              return 0
            })

            // 批量收集需要写入的数据（只收集脏数据）
            const digitalHumansToSave: ChatRecordDigitalHuman[] = []
            const sessionsToSave: ChatRecordSession[] = []
            const messagesToSave: ChatRecordMessage[] = []

            for (const dhId of sortedIds) {
              const dh = this.digitalHumans[dhId]
              if (!dh) continue

              digitalHumansToSave.push({
                id: dhId,
                name: dh.name,
                avatar: dh.avatar,
                currentSessionId: dh.currentSessionId,
                lastActiveTime: dh.lastActiveTime || Date.now(),
                createdAt: Date.now(),
                updatedAt: Date.now()
              })

              for (const session of dh.sessions) {
                sessionsToSave.push({
                  id: session.id,
                  digitalHumanId: dhId,
                  name: session.name || 'Chat',
                  createdAt: session.createdAt || Date.now(),
                  updatedAt: session.updatedAt || Date.now(),
                  avatar: session.avatar,
                  lastMessage: session.lastMessage
                })

                const messages: ChatRecordMessage[] = (session.messages || []).map((m: any) => ({
                  id: m.id,
                  sessionId: session.id,
                  content: m.content || '',
                  // 翻译相关字段
                  translatedContent: m.translatedContent,
                  translatedLang: m.translatedLang,
                  translated: m.translated,
                  isLoadingTranslate: m.isLoadingTranslate,
                  globalTranslation: m.globalTranslation, // 🔧 新增：全局翻译标记
                  // 生图相关字段
                  image: m.image,
                  video: m.video,
                  coverImage: m.coverImage,
                  hasGeneratedImage: m.hasGeneratedImage,
                  isGeneratingImage: m.isGeneratingImage,
                  isSuccessful: m.isSuccessful, // 🔧 修复：保存生图成功状态
                  imageProgress: m.imageProgress,
                  // 基础字段
                  type: m.type || 'user',
                  status: m.status || 'sent',
                  timestamp: m.timestamp || Date.now(),
                  username: m.username,
                  avatar: m.avatar,
                  isTyping: m.isTyping,
                  msgId: m.msgId
                }))
                messagesToSave.push(...messages)
              }
            }

            // 批量写入（使用 Promise.all 并行执行）
            if (
              digitalHumansToSave.length > 0 ||
              sessionsToSave.length > 0 ||
              messagesToSave.length > 0
            ) {
              await Promise.all([
                chatRecordsDB.saveDigitalHumans(digitalHumansToSave),
                chatRecordsDB.saveSessions(sessionsToSave),
                messagesToSave.length > 0
                  ? chatRecordsDB.saveMessages(messagesToSave)
                  : Promise.resolve()
              ])

              // 清除已保存的脏数据标记
              sortedIds.forEach(id => this._dirtyDigitalHumans?.delete(id))
            }
          } catch (e) {
            // IndexedDB 写入失败不影响主流程
            console.error('IndexedDB 保存失败:', e)
          }
        }

        // 使用 requestIdleCallback 在浏览器空闲时执行（如果不支持则立即执行）
        if (typeof requestIdleCallback !== 'undefined') {
          requestIdleCallback(idleCallback, { timeout: 2000 })
        } else {
          idleCallback()
        }
      }, SAVE_DEBOUNCE_MS)
    },

    /**
     * 设置全局自动翻译开关
     */
    setAutoTranslate(on: boolean) {
      this.autoTranslate = on

      // 🔧 设置当前数字人的全局翻译状态
      if (this.currentDigitalHumanId && this.digitalHumans[this.currentDigitalHumanId]) {
        this.digitalHumans[this.currentDigitalHumanId].isGlobalTranslationEnabled = on
        console.log(`🌐 设置数字人 ${this.currentDigitalHumanId} 全局翻译: ${on}`)
      }

      this.saveToStorage()
    },

    /**
     * 🔧 新增：检测并恢复页面刷新前的状态
     */
    async checkAndRecoverState() {
      try {
        // 检查是否有等待回复的状态
        if (this.pendingReply) {
          const { digitalHumanId, sessionId, timestamp } = this.pendingReply

          // 仅当待恢复的数字人与当前页面一致时才尝试恢复，避免在进入其他角色页面时被切换
          if (this.currentDigitalHumanId && digitalHumanId !== this.currentDigitalHumanId) {
            return false
          }

          // 检查时间是否在合理范围内（5分钟内）
          const now = Date.now()
          const timeDiff = now - timestamp
          const MAX_RECOVERY_TIME = 5 * 60 * 1000 // 5分钟

          if (timeDiff < MAX_RECOVERY_TIME && digitalHumanId && sessionId) {
            // 不强制切换到其他数字人，只在当前数字人一致时继续往下

            // 切换到对应的会话
            if (this.digitalHumans[digitalHumanId]) {
              const dh = this.digitalHumans[digitalHumanId]
              // 若目标会话不存在，则创建占位会话，避免 currentSession 取不到
              let sess = dh.sessions.find(s => s.id === sessionId)
              if (!sess) {
                sess = {
                  id: sessionId,
                  name: 'Chat',
                  messages: [],
                  createdAt: Date.now(),
                  updatedAt: Date.now(),
                  avatar: dh.avatar,
                  lastMessage: ''
                } as ChatSession
                dh.sessions.push(sess)
              }
              dh.currentSessionId = sessionId
            }

            // 检查最后一条消息是否可恢复：必须是用户发送且非失败
            const session = this.currentSession
            if (session && session.messages.length > 0) {
              const lastMessage = session.messages[session.messages.length - 1]

              // 如果最后一条消息是用户发送的且非失败，尝试通过WS恢复流（不再追加一条新用户消息）
              if (lastMessage.type === 'user' && lastMessage.status !== 'failed') {
                const resumed = await this.resumeAIReplyForLastMessage()
                if (resumed) {
                  // 注意：不要在这里清除 pendingReply，等待 [DONE] 时再清除
                  return true
                }
              } else if (lastMessage.status === 'failed') {
                // 最后一条已失败，则不再恢复，直接清理等待状态
                this.clearPendingReply()
                return false
              }
            }
          } else {
            // 超时，清除等待状态
            this.pendingReply = null
            this.saveToStorage()
          }
        }

        return false // 表示无需恢复
      } catch (error) {
        // 清除可能损坏的状态
        this.pendingReply = null
        this.saveToStorage()
        return false
      }
    },

    /**
     * 初始化：从 IndexedDB 回填（仅在本地没有缓存或需要更完整数据时）
     */
    async initFromIndexedDB() {
      try {
        await chatRecordsDB.init()
        const dhList = await chatRecordsDB.getAllDigitalHumans()
        for (const dh of dhList) {
          if (!this.digitalHumans[dh.id]) {
            this.digitalHumans[dh.id] = {
              id: dh.id,
              name: dh.name || 'AI Assistant',
              avatar: dh.avatar || '/assets/images/discover/dome.png',
              sessions: [],
              currentSessionId: dh.currentSessionId || null,
              lastSyncTime: 0,
              isFromAPI: false,
              unreadCount: 0,
              lastActiveTime: dh.lastActiveTime || Date.now(),
              isLoading: false,
              isTyping: false
            }
          }
          const sessions = await chatRecordsDB.getSessionsByDigitalHuman(dh.id)
          for (const s of sessions) {
            const messages = await chatRecordsDB.getMessagesBySession(s.id)
            const sessionObj: any = {
              id: s.id,
              name: s.name || 'Chat',
              messages: messages as any,
              createdAt: s.createdAt,
              updatedAt: s.updatedAt,
              avatar: s.avatar,
              lastMessage: messages.length ? messages[messages.length - 1].content : undefined
            }
            const exists = this.digitalHumans[dh.id].sessions.find((ss: any) => ss.id === s.id)
            if (!exists) this.digitalHumans[dh.id].sessions.push(sessionObj)
          }

          // 🔧 新增：如果此时已经有通过 API 先行填充的会话，叠加本地翻译状态
          try {
            const existingSessions = this.digitalHumans[dh.id].sessions || []
            // 构建一个快速查找表：sessionId -> Map(messageId -> localMessage) 与 (type::content -> localMessage)
            const localSessionMap: Record<
              string,
              { byId: Map<string, any>; byKey: Map<string, any> }
            > = {}
            for (const s of sessions) {
              const msgs = await chatRecordsDB.getMessagesBySession(s.id)
              const byId = new Map<string, any>(msgs.map((m: any) => [m.id, m]))
              const byKey = new Map<string, any>(
                msgs.map((m: any) => [`${m.type}::${m.content}`, m])
              )
              localSessionMap[s.id] = { byId, byKey }
            }

            for (const serverSession of existingSessions) {
              // 先尝试按相同 sessionId 匹配本地缓存
              const localSet = localSessionMap[serverSession.id]
              if (localSet) {
                serverSession.messages = (serverSession.messages || []).map((m: any) => {
                  const lm =
                    localSet.byId.get(m.id) || localSet.byKey.get(`${m.type}::${m.content}`)
                  if (lm && (lm.translated || lm.translatedContent || lm.translatedLang)) {
                    return {
                      ...m,
                      translated: lm.translated === true,
                      translatedContent: lm.translatedContent || m.translatedContent,
                      translatedLang: lm.translatedLang || m.translatedLang
                    }
                  }
                  return m
                })
                continue
              }

              // 若找不到同 sessionId，则退化到使用"第一段服务器会话"，按内容匹配叠加
              const localAllById = new Map<string, any>()
              const localAllByKey = new Map<string, any>()
              for (const set of Object.values(localSessionMap)) {
                set.byId.forEach((v, k) => localAllById.set(k, v))
                set.byKey.forEach((v, k) => localAllByKey.set(k, v))
              }
              serverSession.messages = (serverSession.messages || []).map((m: any) => {
                const lm = localAllById.get(m.id) || localAllByKey.get(`${m.type}::${m.content}`)
                if (lm && (lm.translated || lm.translatedContent || lm.translatedLang)) {
                  return {
                    ...m,
                    translated: lm.translated === true,
                    translatedContent: lm.translatedContent || m.translatedContent,
                    translatedLang: lm.translatedLang || m.translatedLang
                  }
                }
                return m
              })
            }
          } catch (_) { }
        }
        this.saveToStorage()
      } catch (e) {
        // 忽略 IndexedDB 读取失败
      }
    },

    /**
     * 🔧 新增：设置等待回复状态
     */
    setPendingReply(digitalHumanId: string, sessionId: string) {
      this.pendingReply = {
        digitalHumanId,
        sessionId,
        timestamp: Date.now()
      }
      this.saveToStorage()
    },

    /**
     * 🔧 新增：清除等待回复状态
     */
    clearPendingReply() {
      this.pendingReply = null
      this.saveToStorage()
    },

    /**
     * 🔧 新增：检查并清理过期的等待回复状态
     * 场景：发送消息后立即退出 App，AI 在后台回复完成，重新进入时状态还在
     * @param digitalHumanId 要检查的数字人ID
     */
    async checkAndClearExpiredStates(digitalHumanId: string) {
      console.log(`🔍 检查数字人 ${digitalHumanId} 的等待回复状态`)

      const digitalHuman = this.digitalHumans[digitalHumanId]
      if (!digitalHuman) {
        console.log('⚠️ 数字人不存在')
        return
      }

      // 检查是否有等待回复状态
      const hasPendingReply = this.pendingReply?.digitalHumanId === digitalHumanId
      const hasWaitingReplies = this.hasWaitingReplies(digitalHumanId)
      const hasLoadingOrTyping = digitalHuman.isLoading || digitalHuman.isTyping

      if (!hasPendingReply && !hasWaitingReplies && !hasLoadingOrTyping) {
        console.log('✅ 无等待回复状态，无需处理')
        return
      }

      // 检查等待时间是否超时（2 分钟）
      const TIMEOUT = 2 * 60 * 1000 // 2 分钟
      const now = Date.now()
      let isExpired = false

      if (this.pendingReply && this.pendingReply.digitalHumanId === digitalHumanId) {
        const waitTime = now - this.pendingReply.timestamp
        if (waitTime > TIMEOUT) {
          console.log(`⏰ pendingReply 已超时 (${Math.floor(waitTime / 1000)}秒)`)
          isExpired = true
        }
      }

      if (!isExpired) {
        const waitingReplies = this.getWaitingReplies(digitalHumanId)
        for (const waiting of waitingReplies) {
          const waitTime = now - waiting.timestamp
          if (waitTime > TIMEOUT) {
            console.log(`⏰ waitingReply 已超时 (${Math.floor(waitTime / 1000)}秒)`)
            isExpired = true
            break
          }
        }
      }

      if (!isExpired) {
        console.log('✅ 等待回复状态未超时，无需处理')
        return
      }

      // 超时了，主动拉取最新消息
      console.log('🔄 等待回复状态已超时，主动拉取最新消息')

      try {
        // 获取当前最后一条消息
        const currentSession = digitalHuman.sessions.find(
          s => s.id === digitalHuman.currentSessionId
        )
        const lastMessage = currentSession?.messages?.[currentSession.messages.length - 1]

        if (!lastMessage || lastMessage.type !== 'user') {
          console.log('⚠️ 最后一条消息不是用户消息，无需拉取')
        } else {
          // 使用 HTTP 拉取最新消息
          const response = await MessageAPI.getChatMsg({
            roleId: digitalHumanId,
            msgId: lastMessage.id as string
          })

          if (response.code === 0 && response.data?.content) {
            console.log('✅ 成功拉取到 AI 回复')

            // 检查是否已经存在该回复
            const existingAIReply = currentSession?.messages.find(
              (m: any) => m.type === 'ai' && m.id === response.data.id
            )

            if (!existingAIReply && currentSession) {
              // 添加 AI 回复消息
              const aiMessage: ChatMessage = {
                id: response.data.id || `ai_${Date.now()}`,
                content: response.data.content,
                type: MessageType.AI,
                status: MessageStatus.RECEIVED,
                timestamp: response.data.timestamp || Date.now(),
                username: digitalHuman.name,
                avatar: digitalHuman.avatar,
                isTyping: false
              }

              currentSession.messages.push(aiMessage)
              currentSession.updatedAt = Date.now()
              currentSession.lastMessage = response.data.content

              console.log('✅ 已添加 AI 回复消息')
            } else {
              console.log('✅ AI 回复消息已存在，无需重复添加')
            }
          } else {
            console.log('⚠️ 未拉取到 AI 回复，可能还未回复完成')
          }
        }
      } catch (error) {
        console.error('❌ 拉取最新消息失败:', error)
      }

      // 清除等待回复状态
      console.log('🧹 清除过期的等待回复状态')

      if (this.pendingReply?.digitalHumanId === digitalHumanId) {
        this.clearPendingReply()
      }

      const waitingReplies = this.getWaitingReplies(digitalHumanId)
      waitingReplies.forEach(waiting => {
        this.removeWaitingReply(digitalHumanId, waiting.sessionId, waiting.userMessageId)
      })

      // 清除 loading/typing 状态
      this.clearDigitalHumanStates(digitalHumanId, {
        loading: false,
        typing: false
      })

      console.log('✅ 过期状态已清理完成')
    },

    /**
     * 更新消息的译文字段（不覆盖原文）
     */
    async updateMessageTranslation(msgId: string | number, translatedText: string, lang?: string) {
      const session = this.currentSession
      if (!session) return

      // 🔧 修复：只匹配 AI 消息，避免匹配到用户消息
      const message: any = session.messages.find(
        (m: any) =>
          m.type === 'ai' &&
          (m.msgId === Number(msgId) || String(m.msgId) === String(msgId) || m.id === String(msgId))
      )
      if (!message) {
        console.warn('未找到 AI 消息，msgId:', msgId)
        return
      }

      message.translatedContent = translatedText
      if (lang) message.translatedLang = lang
      message.translated = true

      session.updatedAt = Date.now()

      // 🔧 性能优化：标记当前数字人数据已变更
      if (this.currentDigitalHumanId) {
        this.markDigitalHumanDirty(this.currentDigitalHumanId)
      }

      await this.saveToStorage()

      // 同步写入 IndexedDB（使用消息的 id 作为 IndexedDB 的 key）
      try {
        await chatRecordsDB.init()
        await chatRecordsDB.updateMessage(message.id, {
          translatedContent: translatedText,
          translatedLang: lang,
          translated: true
        })
      } catch (_) { }
    },

    /**
     * 🔧 新增：自动翻译消息（当开启自动翻译时调用）
     */
    async autoTranslateMessage(message: any, session: ChatSession) {
      if (!message || !message.content || message.translatedContent) {
        return // 已有翻译内容，跳过
      }

      try {
        const { shortLang } = useLanguage()
        const targetLanguage = shortLang.value
        this.translateMessageLoading = 'loading'
        // 调用翻译API
        const res = await MessageAPI.translate({
          content: message.content,
          src: 'auto',
          to: targetLanguage
        })

        if (res?.data) {
          this.translateMessageLoading = 'success'

          const translatedText = res.data
          const msgId = message.msgId || message.id

          // 更新消息的翻译内容
          message.translatedContent = translatedText
          message.translated = true
          message.translatedLang = targetLanguage

          // 更新会话时间戳
          session.updatedAt = Date.now()

          // 保存到 IndexedDB
          try {
            await chatRecordsDB.init()
            await chatRecordsDB.updateMessage(message.id, {
              translatedContent: translatedText,
              translatedLang: targetLanguage,
              translated: true
            })
          } catch (_) { }

          // 标记为脏数据，触发保存
          if (this.currentDigitalHumanId) {
            this.markDigitalHumanDirty(this.currentDigitalHumanId)
          }
          await this.saveToStorage()

          console.log('✅ 自动翻译完成:', msgId)
          // 短暂展示 success 后置为 done
          setTimeout(() => {
            this.translateMessageLoading = 'done'
          }, 800)
        }
      } catch (error: any) {
        message.translated = false
        console.error('自动翻译失败:', error)
        // 翻译失败不影响主流程，静默处理
        this.translateMessageLoading = 'error'
        setTimeout(() => {
          this.translateMessageLoading = 'done'
        }, 1200)
      }
      finally {
        setTimeout(() => {
          this.translateMessageLoading = 'done'
        }, 1200)
      }
    },

    /**
     * 仅设置消息的 translated 标志并持久化（不改译文内容）
     */
    async setMessageTranslated(msgId: string | number, flag: boolean) {
      const session = this.currentSession
      if (!session) return
      // 🔧 修复：只匹配 AI 消息，避免匹配到用户消息
      const message: any = session.messages.find(
        (m: any) =>
          m.type === 'ai' &&
          (m.msgId === Number(msgId) || String(m.msgId) === String(msgId) || m.id === String(msgId))
      )
      if (!message) {
        console.warn('未找到 AI 消息，msgId:', msgId)
        return
      }
      message.translated = !!flag
      session.updatedAt = Date.now()
      await this.saveToStorage()
      try {
        await chatRecordsDB.init()
        // 使用消息的 id 作为 IndexedDB 的 key
        await chatRecordsDB.updateMessage(message.id, { translated: !!flag })
      } catch (_) { }
    },

    /**
     * 根据当前会话中的 AI 消息触发生图（真正图片由 sse 推送回来）
     */
    async generateImageFromMessage(params: any, isVideo: boolean = false) {
      const session = this.currentSession
      if (!session) return

      // 标记当前数字人有正在进行的生图任务（切换数字人时用于保护本地状态）
      const currentDH = this.currentDigitalHuman
      if (currentDH) {
        currentDH.hasActiveImageTask = true
      }

      // 点击时立刻打开生图骨架屏
      this.isGeneratingImage = true

      try {
        if (isVideo) {
          await MessageAPI.submitRecordVideo(params)
        } else {
          await MessageAPI.submitRecordImag(params)
        }
      } catch (e) {
        console.log(e, 'error')

        // 生图触发失败时，直接关闭骨架屏
        this.isGeneratingImage = false
        // 同时清除当前数字人的生图任务标记
        if (currentDH) {
          currentDH.hasActiveImageTask = false
        }
        throw e
      }
    },

    /**
     * 清空本地缓存（IndexedDB + 轻量 localStorage）并重置内存状态
     */
    async clearLocalCache() {
      try {
        await chatRecordsDB.init()
        await chatRecordsDB.clearAll()
      } catch (_) { }

      try {
        localStorage.removeItem('ai-chat-ephemeral')
        localStorage.removeItem('ai-chat-store')
      } catch (_) { }

      // 重置内存状态
      this.digitalHumans = {}
      this.currentDigitalHumanId = null
      this.pendingReply = null
      this.waitingReplies = {}
      this.currentMessage = ''
      this.saveToStorage()
    },

    /**
     * 🔧 新增：恢复因刷新中断的AI回复（不重复追加用户消息）
     */
    async resumeAIReplyForLastMessage(): Promise<boolean> {
      const current = this.currentDigitalHuman
      const session = this.currentSession
      if (!current || !session || session.messages.length === 0) return false

      const lastMessage = session.messages[session.messages.length - 1]
      if (!lastMessage || lastMessage.type !== 'user' || !lastMessage.content?.trim()) {
        return false
      }

      // 标记等待回复状态，便于 shouldProcessMessage 放行
      if (this.currentDigitalHumanId && session.id) {
        this.setPendingReply(this.currentDigitalHumanId, session.id)
      }

      // 设置加载状态（不显示typing，直到有片段到来）
      this.clearDigitalHumanStates(this.currentDigitalHumanId as string, {
        loading: true,
        typing: false
      })

      // ✅ 消息已通过 HTTP API 发送，SSE 负责接收流式响应
      // 直接返回 true，由 SSE 回调处理后续逻辑
      return true
    },

    /**
     * 🔧 新增：让 AI 继续对话（发送"继续"指令但不显示用户消息）
     */
    async continueAIReply(isContinue: boolean): Promise<boolean> {
      const current = this.currentDigitalHuman
      const session = this.currentSession
      if (!current || !session) {
        console.warn('⚠️ 当前没有活跃的会话')
        return false
      }

      // 如果 AI 正在打字，不允许继续
      if (this.isTyping) {
        console.warn('⚠️ AI 正在回复中，请等待')
        return false
      }

      // 目标角色ID
      const targetRoleId =
        this.currentDigitalHumanId === 'default' ? '378' : (this.currentDigitalHumanId as string)

      // 设置等待回复状态
      if (this.currentDigitalHumanId && session.id) {
        this.setPendingReply(this.currentDigitalHumanId, session.id)
      }

      // 设置加载状态，显示打字动画
      if (this.currentDigitalHumanId) {
        this.clearDigitalHumanStates(this.currentDigitalHumanId, {
          loading: true,
          typing: false
        })
      }

      // 发送"继续"指令到后端（不在界面显示用户消息）
      const continuePrompt = 'continue'

      try {
        // 调用后端 API
        const { shortLang } = useLanguage()
        const langParam = this.autoTranslate ? shortLang.value : ''
        const sendMessageAPI = await MessageAPI.sendMessage({
          content: continuePrompt,
          roleId: targetRoleId,
          language: langParam,
          autoTranslate: this.autoTranslate ? 1 : 0,
          isContinue: isContinue,
          // notStream: true
        })
        const continueRes = await sendMessageAPI

        // ✅ Continue 接口请求成功后，启动无回复埋点定时器
        sendBurialPoint.startNoReplyTrackingTimer40s()
        sendBurialPoint.startNoReplyTrackingTimer30s()
        sendBurialPoint.startNoReplyTrackingTimer20s()
        sendBurialPoint.startNoReplyTrackingTimer15s()
        sendBurialPoint.startNoReplyTrackingTimer10s()

        // 🔧 设置20秒超时，超时后开始轮询拉取单条消息记录
        let pollingTimerId: NodeJS.Timeout | null = null
        let retryCount = 0
        const MAX_RETRY_COUNT = 30 // 最多重试30次（总共60秒）
        const continueTimestamp = Date.now() // 记录 Continue 的时间戳

        this.currentTimeoutId = setTimeout(
          async () => {
            const userMsgId = continueRes?.data?.msgId
            if (!userMsgId) {
              console.warn('⚠️ 没有用户消息 ID，无法拉取')
              this.clearDigitalHumanStates(targetRoleId, {
                loading: false,
                typing: false
              })
              this.currentTimeoutId = null
              return
            }

            console.log('⏰ Continue 超时20秒，检查是否需要轮询拉取单条消息记录')

            // 🔧 在轮询前检查：SSE 可能已经收到了 AI 回复
            const targetDigitalHuman = this.digitalHumans[targetRoleId]
            const currentSession = targetDigitalHuman?.sessions?.find(
              s => s.id === targetDigitalHuman.currentSessionId
            )

            // 检查会话中最后一条消息是否是 AI 回复，且时间戳在 Continue 之后
            const lastMsg = currentSession?.messages?.[currentSession.messages.length - 1]
            if (lastMsg?.type === 'ai' && lastMsg.timestamp > continueTimestamp) {
              console.log('✅ SSE 已经收到 AI 回复，无需轮询')
              // ✅ 清除所有无回复埋点定时器
              sendBurialPoint.clearNoReplyTrackingTimer()
              this.clearDigitalHumanStates(targetRoleId, {
                loading: false,
                typing: false
              })
              this.currentTimeoutId = null
              return
            }

            console.log('🔄 开始轮询拉取单条消息记录')

            // 轮询函数：每2秒尝试一次，直到成功或达到最大重试次数
            const pollMessage = async () => {
              const targetDigitalHuman = this.digitalHumans[targetRoleId]

              // 如果状态已经不是加载中，说明已经收到消息或用户取消了，停止轮询
              if (
                !targetDigitalHuman ||
                (!targetDigitalHuman.isLoading && !targetDigitalHuman.isTyping)
              ) {
                if (pollingTimerId) {
                  clearInterval(pollingTimerId)
                  pollingTimerId = null
                }
                return
              }

              // 🔧 每次轮询前检查：SSE 是否已经收到了消息
              const currentSession = targetDigitalHuman?.sessions?.find(
                s => s.id === targetDigitalHuman.currentSessionId
              )
              const lastMsg = currentSession?.messages?.[currentSession.messages.length - 1]
              if (lastMsg?.type === 'ai' && lastMsg.timestamp > continueTimestamp) {
                console.log('✅ SSE 已收到消息，停止轮询')
                // ✅ 清除所有无回复埋点定时器
                sendBurialPoint.clearNoReplyTrackingTimer()
                if (pollingTimerId) {
                  clearInterval(pollingTimerId)
                  pollingTimerId = null
                }
                this.clearDigitalHumanStates(targetRoleId, {
                  loading: false,
                  typing: false
                })
                return
              }

              retryCount++
              console.log(`🔄 第 ${retryCount} 次尝试拉取消息 (Continue)...`)

              try {
                const chatMsgRes = await MessageAPI.getChatMsg({
                  msgId: userMsgId
                })

                const msgData = chatMsgRes?.data
                const aiReply = msgData?.content
                const messageRole = msgData?.role

                // 🔧 修复：只处理 AI 回复消息，跳过用户消息
                if (aiReply && messageRole !== 'user') {
                  console.log('✅ 成功拉取到 AI 回复 (Continue)')

                  sendBurialPoint.longReplyFetchSuccess()
                  // ✅ 收到AI回复，清除所有无回复埋点定时器
                  sendBurialPoint.clearNoReplyTrackingTimer()

                  // 停止轮询
                  if (pollingTimerId) {
                    clearInterval(pollingTimerId)
                    pollingTimerId = null
                  }

                  // 添加 AI 回复消息
                  const aiMessage = this.addMessage({
                    content: aiReply,
                    type: 'ai' as MessageType,
                    status: 'received' as MessageStatus,
                    username: this.currentDigitalHuman?.name || 'AI',
                    avatar: this.currentDigitalHuman?.avatar || ''
                  })

                  // 设置消息 ID
                  if (aiMessage && msgData?.msgId) {
                    ; (aiMessage as any).id = String(msgData.msgId)
                  }

                  // 清理加载状态
                  this.clearDigitalHumanStates(targetRoleId, {
                    loading: false,
                    typing: false
                  })
                } else if (messageRole === 'user') {
                  // 如果返回的是用户消息，继续等待 AI 回复
                  console.log('⏳ 拉取到用户消息，继续等待 AI 回复...')
                } else if (retryCount >= MAX_RETRY_COUNT) {
                  // 达到最大重试次数，停止轮询
                  console.warn('⚠️ 达到最大重试次数，停止轮询')
                  // 拉取超过60秒都没有消息
                  sendBurialPoint.noMessagePulled()
                  // ✅ 清除所有无回复埋点定时器
                  sendBurialPoint.clearNoReplyTrackingTimer()
                  if (pollingTimerId) {
                    clearInterval(pollingTimerId)
                    pollingTimerId = null
                  }

                  // 显示超时提示
                  const randomKey =
                    timeoutPromptWord[Math.floor(Math.random() * timeoutPromptWord.length)]

                  this.addMessage({
                    content: this.getTranslation(randomKey || I18N_KEYS.SSE_TIMEOUT),
                    type: 'system' as MessageType,
                    status: 'received' as MessageStatus,
                    username: 'System'
                  })

                  this.clearDigitalHumanStates(targetRoleId, {
                    loading: false,
                    typing: false
                  })
                } else {
                  console.log('⏳ 未获取到数据，2秒后重试...')
                }
              } catch (error) {
                console.error(`❌ 第 ${retryCount} 次拉取失败:`, error)
                sendBurialPoint.longReplyFetchFailed()
                if (retryCount >= MAX_RETRY_COUNT) {
                  // 达到最大重试次数
                  // ✅ 清除所有无回复埋点定时器
                  sendBurialPoint.clearNoReplyTrackingTimer()
                  if (pollingTimerId) {
                    clearInterval(pollingTimerId)
                    pollingTimerId = null
                  }

                  const randomKey =
                    timeoutPromptWord[Math.floor(Math.random() * timeoutPromptWord.length)]

                  this.addMessage({
                    content: this.getTranslation(randomKey || I18N_KEYS.SSE_TIMEOUT),
                    type: 'system' as MessageType,
                    status: 'received' as MessageStatus,
                    username: 'System'
                  })

                  this.clearDigitalHumanStates(targetRoleId, {
                    loading: false,
                    typing: false
                  })
                }
              }
            }

            // 立即执行第一次
            await pollMessage()

            // 如果第一次没有成功，启动定时轮询（每2秒一次）
            if (!pollingTimerId && retryCount < MAX_RETRY_COUNT) {
              pollingTimerId = setInterval(pollMessage, 2000)
            }

            // 清除超时定时器ID
            this.currentTimeoutId = null
          },
          localStorage.getItem('modelIndex') === '3' ? 15000 : SSE_TIMEOUT_MS
        )

        return true
      } catch (error) {
        this.clearDigitalHumanStates(targetRoleId, {
          loading: false,
          typing: false
        })
        return false
      }
    },

    /**
     * 加载更多聊天记录
     */
    async loadMoreMessages(): Promise<boolean> {
      if (this.pagination.isLoadingMore || !this.pagination.hasMore) {
        return false
      }

      const currentDigitalHuman = this.currentDigitalHuman
      if (!currentDigitalHuman || !this.currentSession) {
        return false
      }

      try {
        this.pagination.isLoadingMore = true

        // 🔧 先尝试从本地缓存加载更多消息
        const currentSession = this.currentSession

        // 获取所有会话的所有消息（按时间排序）
        const allMessages = currentDigitalHuman.sessions
          .filter(session => session?.messages && Array.isArray(session.messages))
          .flatMap(session => session.messages)
          .filter(msg => {
            if (!msg || !msg.id) return false
            const isTemporaryId =
              isNaN(Number(msg.id)) || msg.id.includes('_') || msg.id.includes('-')
            if (isTemporaryId) return false
            return true
          })

        // 去重并排序
        const uniqueMessages: any[] = []
        const seenIds = new Set()
        allMessages.forEach(msg => {
          if (!seenIds.has(msg.id)) {
            seenIds.add(msg.id)
            uniqueMessages.push(msg)
          }
        })
        uniqueMessages.sort((a, b) => a.timestamp - b.timestamp)

        // 🔧 从本地加载更多：取当前显示消息之前的20条
        const currentFirstTimestamp = currentSession.messages[0]?.timestamp || Date.now()
        const olderMessages = uniqueMessages
          .filter(msg => msg.timestamp < currentFirstTimestamp)
          .slice(-20) // 取最近的20条旧消息

        if (olderMessages.length > 0) {
          // 🔧 去重：过滤掉已经存在的消息
          const existingIds = new Set(currentSession.messages.map(m => m.id))
          const newOlderMessages = olderMessages.filter(msg => !existingIds.has(msg.id))

          if (newOlderMessages.length > 0) {
            // 将旧消息添加到当前会话的开头
            currentSession.messages = [...newOlderMessages, ...currentSession.messages]

            // 检查是否还有更多本地消息
            const oldestDisplayedTimestamp = newOlderMessages[0].timestamp
            const hasMoreLocal = uniqueMessages.some(
              msg => msg.timestamp < oldestDisplayedTimestamp
            )

            this.pagination.page += 1
            this.pagination.hasMore = hasMoreLocal

            this.saveToStorage()
            console.log(
              `✅ 从本地加载了 ${newOlderMessages.length} 条历史消息${hasMoreLocal ? '（还有更多）' : '（已全部加载）'}`
            )
            return true
          } else {
            // 所有消息都已经存在，标记为没有更多
            this.pagination.hasMore = false
            console.log('⚠️ 所有本地消息都已加载')
            return false
          }
        }

        // 🔧 本地没有更多消息，尝试从 API 加载
        const response = await MessageAPI.getChatRecordsPaginated({
          roleId: Number(currentDigitalHuman.id),
          page: this.pagination.page + 1,
          pageSize: this.pagination.pageSize
        })

        if (response && response.data.length > 0) {
          // 转换API数据格式
          const newMessages = response.data.map((record: any) => {
            const isUser = record.role === 'user'
            const image = record.image

            // 🔧 处理内容：如果包含 </content>，只保留到 </content> 为止（只处理 AI 消息）
            const cleanContent = isUser
              ? record.content
              : this.processContentEnd(record.content || '')

            return {
              id: record.id.toString(),
              content: cleanContent,
              type: isUser ? 'user' : ('ai' as MessageType),
              status: 'received' as MessageStatus,
              timestamp: record.createdAt * 1000, // 转换为毫秒
              username: isUser ? 'You' : 'AI Assistant',
              avatar: isUser ? undefined : '/assets/images/discover/dome.png',
              image,
              video: record.video,
              coverImage: record.coverImage,
              // 🔧 生图状态还原：如果历史记录中有图片，认为这条消息已经生过图
              hasGeneratedImage: !!image,
              isGeneratingImage: false,
              // 🔧 生图成功标记：如果有图片则为成功，没有图片则为 undefined（未生图）
              isSuccessful: image ? true : undefined
            }
          })

          // 🔧 去重：过滤掉已经存在的消息
          const existingIds = new Set(currentSession.messages.map(m => m.id))
          const filteredNewMessages = newMessages.filter(
            (msg: ChatMessage) => !existingIds.has(msg.id)
          )

          // 将新消息添加到当前会话的开头（历史消息）
          if (filteredNewMessages.length > 0) {
            currentSession.messages = [...filteredNewMessages, ...currentSession.messages]
          }

          // 更新分页状态
          this.pagination.page += 1
          this.pagination.hasMore = response.data.length === this.pagination.pageSize

          this.saveToStorage()
          console.log(`✅ 从 API 加载了 ${filteredNewMessages.length} 条历史消息（去重后）`)
          return true
        } else {
          // 没有更多数据
          this.pagination.hasMore = false
          console.log('✅ 没有更多历史消息了')
          return false
        }
      } catch (error) {
        console.error('加载更多消息失败:', error)
        return false
      } finally {
        this.pagination.isLoadingMore = false
      }
    },

    /**
     * 重置分页状态
     */
    resetPagination() {
      this.pagination.page = 1
      this.pagination.hasMore = false
      this.pagination.isLoadingMore = false
    },

    /**
     * 从API加载数字人数据
     */
    async loadDigitalHumanFromAPI(id: string): Promise<any> {
      try {
        // 确定要使用的roleId
        const actualRoleId = id

        // 重置分页状态
        this.resetPagination()

        // 首次加载使用分页API获取第一页聊天记录
        const response = await MessageAPI.getChatRecordsPaginated({
          roleId: Number(actualRoleId),
          page: 1,
          pageSize: this.pagination.pageSize
        })

        // 转换API数据格式为store需要的格式
        if (response && response.data.length > 0) {
          const chatRecords = response.data

          // 设置分页状态
          this.pagination.page = 1
          this.pagination.hasMore = response.data.length === this.pagination.pageSize

          // 将API的聊天记录转换为ChatMessage格式
          const messages = chatRecords.map((record: any) => {
            const isUser = record.role === 'user'
            const image = record.image

            // 🔧 处理内容：如果包含 </content>，只保留到 </content> 为止（只处理 AI 消息）
            const cleanContent = isUser
              ? record.content
              : this.processContentEnd(record.content || '')

            return {
              id: record.id.toString(),
              content: cleanContent,
              type: isUser ? 'user' : ('ai' as MessageType),
              status: 'received' as MessageStatus,
              timestamp: record.createdAt * 1000, // 转换为毫秒
              username: isUser ? 'You' : 'AI Assistant',
              avatar: isUser ? undefined : '/assets/images/discover/dome.png',
              image,
              video: record.video,
              coverImage: record.coverImage,
              // 🔧 生图状态还原：如果历史记录中有图片，认为这条消息已经生过图
              hasGeneratedImage: !!image,
              isGeneratingImage: false,
              // 🔧 生图成功标记：如果有图片则为成功，没有图片则为 undefined（未生图）
              isSuccessful: image ? true : undefined
            }
          })

          // 创建会话对象
          const session: ChatSession = {
            id: this.generateId(),
            name: 'Chat History',
            messages: messages,
            createdAt:
              messages.length > 0
                ? Math.min(...messages.map((m: ChatMessage) => m.timestamp))
                : Date.now(),
            updatedAt:
              messages.length > 0
                ? Math.max(...messages.map((m: ChatMessage) => m.timestamp))
                : Date.now(),
            avatar: '/assets/images/discover/dome.png',
            lastMessage: messages.length > 0 ? messages[messages.length - 1].content : undefined
          }

          return {
            sessions: [session],
            currentSessionId: session.id
            // 其他数字人信息可以在这里添加
          }
        }

        return null
      } catch (error) {
        return null
      }
    },

    /**
     * 重新生成消息
     */
    /**
     * 重新生成AI消息
     * @param roleId 角色ID
     * @param aiRecordId AI消息的记录ID（msgId）
     * @param messageId 前端消息ID（用于定位要替换的消息）
     */
    async regenerateMessage(roleId: number, aiRecordId: any, messageId?: string) {
      let backupMessage: any = null
      let messageIndex: number = -1
      let session: any = null

      try {
        session = this.currentSession
        if (!session) {
          console.error('❌ 当前会话不存在')
          return null
        }

        // 1. 找到要重新生成的AI消息
        let targetMessage: any = null
        if (messageId) {
          targetMessage = session.messages.find((m: any) => m.id === messageId)
        } else if (aiRecordId) {
          targetMessage = session.messages.find(
            (m: any) => m.type === 'ai' && (m.msgId === aiRecordId || m.id === aiRecordId)
          )
        }

        if (!targetMessage) {
          console.error('❌ 未找到目标消息')
          return null
        }

        console.log('🔄 开始重新生成消息:', {
          roleId,
          aiRecordId,
          messageId: targetMessage.id,
          msgId: targetMessage.msgId
        })

        // 2. 备份消息（用于失败时恢复）
        messageIndex = session.messages.findIndex((m: any) => m.id === targetMessage.id)
        backupMessage = { ...targetMessage } // 深拷贝备份

        if (messageIndex !== -1) {
          session.messages.splice(messageIndex, 1)
          console.log('✅ 已删除旧消息，等待SSE创建新消息')

          // 同时从 IndexedDB 删除旧消息
          try {
            await chatRecordsDB.deleteMessage(targetMessage.id)
            console.log('✅ 已从IndexedDB删除旧消息')
          } catch (error) {
            console.error('❌ 删除IndexedDB消息失败:', error)
          }
        }

        // 🔧 修复：设置等待回复状态，确保SSE收到[DONE]时能正确清除状态
        this.setPendingReply(String(roleId), session.id)

        // 设置数字人的打字状态
        this.clearDigitalHumanStates(String(roleId), {
          loading: true,
          typing: false
        })

        // 保存到存储（localStorage）
        this.saveToStorage()

        // 3. 调用API重新生成
        const response = await MessageAPI.sendMessage({
          roleId: roleId,
          content: 're-generate',
          aiRecordId: Number(aiRecordId),
          // notStream: true
        })

        console.log('✅ 重新生成API响应:', response)

        // 4. SSE会自动处理返回的流式消息
        // handleSSEMessage会通过msgId创建新的消息

        return response
      } catch (error) {
        console.error('❌ 重新生成消息失败:', error)

        // 🔧 修复：恢复备份的消息到原来的位置
        if (backupMessage && messageIndex !== -1) {
          session.messages.splice(messageIndex, 0, backupMessage)
          console.log('✅ 已恢复备份消息到原位置')

          // 重新保存到 IndexedDB
          try {
            await chatRecordsDB.saveMessage(backupMessage)
            console.log('✅ 已重新保存消息到IndexedDB')
          } catch (saveError) {
            console.error('❌ 重新保存IndexedDB消息失败:', saveError)
          }

          // 保存到存储（localStorage）
          this.saveToStorage()
        }

        // 清除等待回复状态
        this.clearPendingReply()

        // 清除打字状态
        if (roleId) {
          this.clearDigitalHumanStates(String(roleId), {
            loading: false,
            typing: false
          })
        }

        throw error
      }
    },
    /**
     * 同步清理所有角色的对话消息，只保留每个会话最新的10条
     */
    async deleteAllDigitalHumanMessages() {
      try {
        const MESSAGES_TO_KEEP = 10 // 每个会话保留的消息数量
        let totalDeletedCount = 0

        console.log('🧹 开始同步清理：每个会话保留最新的10条消息...')

        // 1. 清理IndexedDB中的过期消息（保留每个会话最新的10条）
        await chatRecordsDB.init()

        // 获取所有数字人
        const allDigitalHumans = await chatRecordsDB.getAllDigitalHumans()

        for (const dh of allDigitalHumans) {
          console.log(`🔄 清理数字人 ${dh.name} (${dh.id}) 的消息...`)

          // 获取该数字人的所有会话
          const sessions = await chatRecordsDB.getSessionsByDigitalHuman(dh.id)
          let digitalHumanDeletedCount = 0

          for (const session of sessions) {
            // 获取该会话的所有消息
            const messages = await chatRecordsDB.getMessagesBySession(session.id)

            if (messages.length > MESSAGES_TO_KEEP) {
              // 按时间戳排序（最新的在前）
              messages.sort((a, b) => b.timestamp - a.timestamp)

              // 保留最新的10条，删除其余的
              const messagesToDelete = messages.slice(MESSAGES_TO_KEEP)

              console.log(`📝 会话 ${session.id} 有 ${messages.length} 条消息，删除 ${messagesToDelete.length} 条旧消息`)

              // 批量删除旧消息
              for (const message of messagesToDelete) {
                await chatRecordsDB.deleteMessage(message.id)
                digitalHumanDeletedCount++
              }
            }
          }

          if (digitalHumanDeletedCount > 0) {
            console.log(`✅ 数字人 ${dh.name} 删除了 ${digitalHumanDeletedCount} 条旧消息`)
            totalDeletedCount += digitalHumanDeletedCount
          }
        }

        // 2. 同步清理内存中的消息数据
        for (const digitalHuman of Object.values(this.digitalHumans)) {
          digitalHuman.sessions.forEach(session => {
            if (session.messages && session.messages.length > MESSAGES_TO_KEEP) {
              // 按时间戳排序（最新的在前）
              session.messages.sort((a, b) => b.timestamp - a.timestamp)

              // 保留最新的10条
              const originalLength = session.messages.length
              session.messages = session.messages.slice(0, MESSAGES_TO_KEEP)

              const deletedCount = originalLength - session.messages.length
              if (deletedCount > 0) {
                console.log(`🧹 内存中会话 ${session.id} 删除了 ${deletedCount} 条旧消息`)
                session.updatedAt = Date.now()

                // 更新最后一条消息
                if (session.messages.length > 0) {
                  session.lastMessage = session.messages[0].content
                } else {
                  session.lastMessage = undefined
                }
              }
            }
          })
        }

        // 3. 清除等待回复状态（只保留有效的）
        this.clearPendingReply()

        // 4. 清除无效的等待回复记录
        const validWaitingIds: string[] = []
        Object.entries(this.waitingReplies).forEach(([waitingId, waiting]) => {
          // 检查等待的数字人和会话是否还存在
          const digitalHuman = this.digitalHumans[waiting.digitalHumanId]
          if (digitalHuman) {
            const session = digitalHuman.sessions.find(s => s.id === waiting.sessionId)
            if (session && session.messages.some(m => m.id === waiting.userMessageId)) {
              validWaitingIds.push(waitingId)
            }
          }
        })

        // 删除无效的等待记录
        Object.keys(this.waitingReplies).forEach(waitingId => {
          if (!validWaitingIds.includes(waitingId)) {
            delete this.waitingReplies[waitingId]
          }
        })

        // 5. 清除所有数字人的加载和打字状态
        for (const digitalHuman of Object.values(this.digitalHumans)) {
          digitalHuman.isLoading = false
          digitalHuman.isTyping = false
        }

        // 6. 重置所有数字人的未读消息数量
        for (const digitalHuman of Object.values(this.digitalHumans)) {
          digitalHuman.unreadCount = 0
        }

        // 7. 保存到localStorage（主要保存状态，不包含消息）
        this.saveToStorage()

        if (totalDeletedCount > 0) {
          console.log(`✅ 同步清理完成，共删除了 ${totalDeletedCount} 条过期消息，每个会话保留最新的10条`)
        } else {
          console.log('✅ 同步清理完成，所有会话的消息数量都在正常范围内')
        }
      } catch (error) {
        console.error('❌ 同步清理消息失败:', error)
        throw error
      }
    },

    /**
     * 删除当前角色的所有对话消息（包括本地存储）
     */
    async deleteCurrentDigitalHumanMessages() {
      const currentDigitalHuman = this.currentDigitalHuman
      if (!currentDigitalHuman) {
        console.warn('⚠️ 当前没有数字人，无法删除消息')
        return
      }

      const currentDigitalHumanId = this.currentDigitalHumanId
      if (!currentDigitalHumanId) {
        console.warn('⚠️ 当前没有数字人ID，无法删除消息')
        return
      }

      console.log(`🗑️ 开始删除数字人 ${currentDigitalHuman.name} 的所有对话消息和本地记录`)

      try {
        // 1. 删除IndexedDB中的本地聊天记录
        await chatRecordsDB.init()
        await chatRecordsDB.deleteDigitalHumanData(currentDigitalHumanId)

        // 2. 清空内存中的消息数据
        currentDigitalHuman.sessions.forEach(session => {
          if (session.messages) {
            session.messages = []
            session.lastMessage = undefined
            session.updatedAt = Date.now()
          }
        })

        // 3. 清除等待回复状态
        this.clearPendingReply()

        // 4. 清除当前数字人的所有等待回复记录
        const waitingReplies = this.getWaitingReplies(currentDigitalHumanId)
        waitingReplies.forEach(waiting => {
          this.removeWaitingReply(currentDigitalHumanId, waiting.sessionId, waiting.userMessageId)
        })

        // 5. 清除加载和打字状态
        this.clearDigitalHumanStates(currentDigitalHumanId, {
          loading: false,
          typing: false
        })

        // 6. 重置未读消息数量
        currentDigitalHuman.unreadCount = 0

        // 7. 重置分页状态
        this.resetPagination()

        // 8. 保存到localStorage（主要保存状态，不包含消息）
        this.saveToStorage()

        console.log(`✅ 已删除数字人 ${currentDigitalHuman.name} 的所有对话消息和本地记录`)
      } catch (error) {
        console.error(`❌ 删除数字人消息失败:`, error)
        throw error
      }
    },

    /**
     * 检查数据是否需要刷新（5分钟内的数据认为是新鲜的）
     */
    isDataFresh(digitalHuman: DigitalHuman): boolean {
      if (!digitalHuman.lastSyncTime) return false
      const now = Date.now()
      const fiveMinutes = 5 * 60 * 1000
      return now - digitalHuman.lastSyncTime < fiveMinutes
    },

    /**
     * 初始化数字人（智能缓存策略）
     */
    async initDigitalHuman(id: string, name: string, avatar: string) {
      const existing = this.digitalHumans[id]

      // 1. 如果数据存在且新鲜，直接使用
      if (existing && this.isDataFresh(existing)) {
        return
      }

      // 2. 如果数据存在但不新鲜，在后台刷新
      if (existing && !this.isDataFresh(existing)) {
        // 后台刷新，不阻塞用户操作
        this.refreshDigitalHuman(id)
        return
      }

      // 3. 如果数据不存在，从API加载

      const apiData = await this.loadDigitalHumanFromAPI(id)

      if (apiData) {
        // API有数据，使用API数据

        this.digitalHumans[id] = {
          id,
          name: apiData.name || name,
          avatar: apiData.avatar || avatar,
          sessions: apiData.sessions || [],
          currentSessionId: apiData.currentSessionId || null,
          lastSyncTime: Date.now(),
          isFromAPI: true,
          unreadCount: 0, // 🔧 新增：初始化未读消息数量
          lastActiveTime: Date.now(), // 🔧 新增：初始化最后活跃时间
          isLoading: false, // 🔧 新增：初始化加载状态
          isTyping: false // 🔧 新增：初始化打字状态
        }

        // 覆盖 API 数据中的每条消息译文（若本地 IndexedDB 有译文）
        await this.overlayTranslationsFromIndexedDB(id)
      } else {
        // API没有数据，创建新的空数字人

        this.digitalHumans[id] = {
          id,
          name,
          avatar,
          sessions: [],
          currentSessionId: null,
          lastSyncTime: Date.now(),
          isFromAPI: false,
          unreadCount: 0, // 🔧 新增：初始化未读消息数量
          lastActiveTime: Date.now(), // 🔧 新增：初始化最后活跃时间
          isLoading: false, // 🔧 新增：初始化加载状态
          isTyping: false // 🔧 新增：初始化打字状态
        }
      }

      this.saveToStorage()
    },

    /**
     * 🔧 新增：合并所有会话的消息到一个"全部记录"会话
     * 按时间排序，通过 id 去重，过滤临时 id
     */
    mergeAllSessionMessages(digitalHumanId: string): void {
      const digitalHuman = this.digitalHumans[digitalHumanId]
      if (!digitalHuman) {
        console.log('⚠️ 数字人不存在，跳过消息合并')
        return
      }

      console.log('📝 开始合并所有会话消息...')

      // 判断 id 是否为临时 id
      const isTemporaryId = (id: string): boolean => {
        if (!id) return true
        return isNaN(Number(id)) || id.includes('_') || id.includes('-')
      }

      if (digitalHuman.sessions.length === 0) {
        // 没有会话，创建新会话
        this.createSession('聊天记录')
        return
      }

      // 收集所有会话的消息
      const allMessages = digitalHuman.sessions
        .filter(session => session?.messages && Array.isArray(session.messages))
        .flatMap(session => session.messages)
        .filter(msg => {
          // 过滤条件：不是临时 id，id 不是 NaN
          if (!msg || !msg.id) return false
          if (isTemporaryId(msg.id)) return false
          if (isNaN(Number(msg.id))) return false
          return true
        })

      if (allMessages.length === 0) {
        console.log('📭 无有效消息需要合并')
        return
      }

      // 按 id 去重
      const uniqueMessages = allMessages.reduce((acc: any[], msg: any) => {
        const exists = acc.find(m => m.id === msg.id)
        if (!exists) {
          acc.push(msg)
        }
        return acc
      }, [])

      console.log(`✅ 去重后剩余 ${uniqueMessages.length} 条消息`)

      // 按时间排序（从旧到新）
      uniqueMessages.sort((a, b) => a.timestamp - b.timestamp)

      // 创建或更新"全部记录"会话
      const ALL_MESSAGES_SESSION_ID = `${digitalHumanId}_all_messages`
      let allSession = digitalHuman.sessions.find(s => s.id === ALL_MESSAGES_SESSION_ID)

      // 🔧 性能优化：只展示最近的50条消息，避免内存过度占用
      const MAX_DISPLAY_MESSAGES = 20
      const displayMessages = uniqueMessages.slice(-MAX_DISPLAY_MESSAGES)

      // 🔧 如果总消息数大于显示数量，说明还有更多历史消息可以加载
      const hasMoreHistory = uniqueMessages.length >= MAX_DISPLAY_MESSAGES

      if (!allSession) {
        // 创建"全部记录"会话
        allSession = {
          id: ALL_MESSAGES_SESSION_ID,
          name: '聊天记录',
          messages: displayMessages,
          createdAt: Date.now(),
          updatedAt: Date.now(),
          avatar: digitalHuman.avatar
        }
        digitalHuman.sessions = [allSession]
      } else {
        // 更新"全部记录"会话
        allSession.messages = displayMessages
        allSession.updatedAt = Date.now()
        // 保留"全部记录"会话，移除其他会话
        digitalHuman.sessions = [allSession]
      }

      // 设置当前会话为"全部记录"
      digitalHuman.currentSessionId = ALL_MESSAGES_SESSION_ID

      // 🔧 设置分页状态：如果本地有更多消息，标记可以加载更多
      if (hasMoreHistory) {
        this.pagination.hasMore = true
        this.pagination.page = 1
      }

      console.log(
        `✅ 消息合并完成，展示最近 ${displayMessages.length} 条消息，总共有 ${uniqueMessages.length} 条消息${hasMoreHistory ? '（可加载更多）' : ''}`
      )
    },

    /**
     * 🔧 静默同步：用接口数据完全覆盖本地数据
     * 接口数据是权威的，静默替换本地可能不正确的数据（不删除，直接覆盖）
     */
    async incrementalSyncMessages(digitalHumanId: string): Promise<void> {
      try {
        const digitalHuman = this.digitalHumans[digitalHumanId]
        if (!digitalHuman) {
          console.log('⚠️ 数字人不存在，跳过同步')
          return
        }

        // 🔧 保护：流式回复进行中或生图进行中时，跳过同步
        if (
          digitalHuman.isTyping ||
          digitalHuman.hasActiveImageTask ||
          (this.pendingReply && this.pendingReply.digitalHumanId === digitalHumanId)
        ) {
          console.log('⏸ 跳过同步：当前AI正在回复或生图中')
          return
        }

        // 获取或创建 all_messages 会话
        let allSession = digitalHuman.sessions.find(s => s.id === `${digitalHumanId}_all_messages`)
        if (!allSession) {
          allSession = {
            id: `${digitalHumanId}_all_messages`,
            name: 'All Messages',
            messages: [],
            createdAt: Date.now(),
            updatedAt: Date.now(),
            avatar: digitalHuman.avatar
          }
          digitalHuman.sessions.push(allSession)
        }

        // 分页获取所有服务器消息
        const allServerMessages: any[] = []

        console.log('🔄 开始从服务器获取最近20条消息...')

        const response = await MessageAPI.getChatRecordsPaginated({
          roleId: Number(digitalHumanId),
          page: 1,
          pageSize: 20
        })

        // 转换服务器消息格式
        const serverMessages = response.data.map((record: any) => {
          const isUser = record.role === 'user'
          const image = record.image
          const cleanContent = isUser
            ? record.content
            : this.processContentEnd(record.content || '')

          return {
            id: record.id.toString(),
            content: cleanContent,
            type: isUser ? MessageType.USER : MessageType.AI,
            status: MessageStatus.RECEIVED,
            timestamp: record.createdAt * 1000,
            username: isUser ? 'You' : 'AI Assistant',
            avatar: isUser ? undefined : '/assets/images/discover/dome.png',
            image,
            video: record.video,
            coverImage: record.coverImage,
            hasGeneratedImage: !!image,
            isGeneratingImage: false,
            isSuccessful: image ? true : undefined
          }
        })

        allServerMessages.push(...serverMessages)

        if (allServerMessages.length === 0) {
          console.log('✅ 服务器无消息')
          // 如果服务器无消息，清空本地消息
          allSession.messages = []
          await this.saveToStorage()
          return
        }

        // 服务器消息按时间排序（从旧到新）
        allServerMessages.sort((a: any, b: any) => a.timestamp - b.timestamp)

        console.log(`📥 从服务器获取到 ${allServerMessages.length} 条消息，开始覆盖本地数据...`)

        // 🔧 关键：直接用服务器数据覆盖本地数据（不删除，直接替换）
        // 在将服务器数据写入 store 之前，先尝试从 IndexedDB 中叠加已存在的译文，
        // 以避免进入页面后再异步覆盖导致的 UI 跳动。
        await chatRecordsDB.init()
        try {
          const localMsgs = await chatRecordsDB.getMessagesBySession(allSession.id)
          if (Array.isArray(localMsgs) && localMsgs.length > 0) {
            const byId = new Map(localMsgs.map((m: any) => [m.id, m]))
            allSession.messages = allServerMessages.map((m: any) => {
              const lm = byId.get(m.id)
              if (lm && (lm.translated || lm.translatedContent || lm.translatedLang)) {
                return {
                  ...m,
                  translated: lm.translated === true,
                  translatedContent: lm.translatedContent || m.translatedContent,
                  translatedLang: lm.translatedLang || m.translatedLang
                }
              }
              return m
            })
          } else {
            allSession.messages = allServerMessages
          }
        } catch (e) {
          // 如果叠加译文失败，则回退为原始服务器数据（保证同步流程不中断）
          allSession.messages = allServerMessages
        }

        // 删除其他临时会话（如果有）
        digitalHuman.sessions = digitalHuman.sessions.filter(
          s => s.id === `${digitalHumanId}_all_messages` || !s.id.includes('_temp_')
        )

        // 更新数字人的最后同步时间
        digitalHuman.lastSyncTime = Date.now()

        // 保存到存储（会自动保存到 IndexedDB，使用 put 操作会自然覆盖同 id 的数据）
        await this.saveToStorage()

        console.log(`✅ 静默同步完成（覆盖 ${allServerMessages.length} 条消息，已尝试叠加本地译文）`)
      } catch (error) {
        console.error('❌ 静默同步失败:', error)
      }
    },

    /**
     * 切换到指定数字人（本地优先，有本地数据就用本地，没有就请求接口）
     * @param id 数字人ID
     * @param name 数字人名称（可选）
     * @param avatar 数字人头像（可选）
     */
    async switchDigitalHumanLocal(id: any, name?: string, avatar?: string) {
      // 处理特殊情况：如果传入的是"default"，获取默认角色ID
      let realDigitalHumanId = id

      if (id === 'default') {
        realDigitalHumanId = '378' // 备用默认值
      }

      const existingDigitalHuman = this.digitalHumans[realDigitalHumanId]

      // 🔧 修复：检查是否有包含消息的会话（而不是检查 sessions 本身是否为数组）
      const sessionsWithMessages =
        existingDigitalHuman?.sessions?.filter(
          (session: any) =>
            session?.messages && Array.isArray(session.messages) && session.messages.length > 0
        ) || []

      // 检查是否有本地数据（至少有一个会话包含消息）
      const hasLocalData =
        existingDigitalHuman &&
        existingDigitalHuman.sessions &&
        existingDigitalHuman.sessions.length > 0 &&
        sessionsWithMessages.length > 0

      // 有本地数据就用本地数据
      if (hasLocalData) {
        // 更新名称和头像（如果提供了新值）
        if (name && existingDigitalHuman.name !== name) {
          existingDigitalHuman.name = name
        }
        if (avatar && existingDigitalHuman.avatar !== avatar) {
          existingDigitalHuman.avatar = avatar
        }

        // 切换到该数字人
        const previousDigitalHumanId = this.currentDigitalHumanId

        // 🔧 修复：切换数字人时，智能清除过期状态
        // 使用统一的 checkAndClearExpiredStates 方法，支持主动拉取消息
        if (previousDigitalHumanId !== realDigitalHumanId) {
          const newDigitalHuman = this.digitalHumans[realDigitalHumanId]
          if (newDigitalHuman) {
            // 🔧 关键修复：检查消息状态，智能判断是否应该清除 loading/typing
            // 异步执行，不阻塞切换流程
            this.checkAndClearExpiredStates(realDigitalHumanId)
          }
        }

        this.currentDigitalHumanId = realDigitalHumanId

        // 标记未读消息
        const waitingRepliesForOtherHumans = Object.values(this.waitingReplies).filter(
          waiting => waiting.digitalHumanId !== realDigitalHumanId
        )

        waitingRepliesForOtherHumans.forEach(waiting => {
          this.incrementUnreadCount(waiting.digitalHumanId)
        })

        // 清除之前数字人的状态
        if (previousDigitalHumanId && previousDigitalHumanId !== realDigitalHumanId) {
          const previousDigitalHuman = this.digitalHumans[previousDigitalHumanId]
          if (previousDigitalHuman && !this.hasWaitingReplies(previousDigitalHumanId)) {
            this.clearDigitalHumanStates(previousDigitalHumanId, {
              loading: false,
              typing: false
            })
          }
        }

        // 标记当前数字人为已读
        this.markCurrentAsRead()

        // 🔧 优化：合并所有会话的消息到一个"全部记录"会话
        this.mergeAllSessionMessages(realDigitalHumanId)

        this.saveToStorage()

        // 🔧 优化：后台增量同步最新消息（类似微信）
        setTimeout(async () => {
          console.log('🔄 开始后台增量同步...')
          await this.incrementalSyncMessages(realDigitalHumanId)
        }, 100)

        return
      }

      // 没有本地数据，请求接口
      await this.switchDigitalHuman(realDigitalHumanId, name, avatar)
    },

    /**
     * 切换到指定数字人（如果不存在则先初始化）
     */
    async switchDigitalHuman(id: any, name?: string, avatar?: string) {
      // 处理特殊情况：如果传入的是"default"，获取默认角色ID
      let realDigitalHumanId = id

      if (id === 'default') {
        try {
          // TODO: 需要导入 getRoleList 函数
          // 临时使用备用默认值
          realDigitalHumanId = '378' // 备用默认值
        } catch (error) {
          realDigitalHumanId = '378' // 备用默认值
        }
      }

      // 1. 🚀 本地优先策略：如果数字人不存在，先创建空数字人立即显示，然后后台加载数据
      if (!this.digitalHumans[realDigitalHumanId]) {
        // 🔧 设置初始化加载状态
        this.isInitializing = true

        // 先创建空的数字人，立即显示
        this.digitalHumans[realDigitalHumanId] = {
          id: realDigitalHumanId,
          name: name || 'AI Assistant',
          avatar: avatar || '/assets/images/discover/dome.png',
          sessions: [],
          currentSessionId: null,
          lastSyncTime: 0,
          isFromAPI: false,
          unreadCount: 0,
          lastActiveTime: Date.now(),
          isLoading: false,
          isTyping: false
        }

        // 立即保存并显示
        this.saveToStorage()

        // 🔧 后台异步加载真实数据
        setTimeout(async () => {
          console.log(`🔄 后台加载聊天记录: ${realDigitalHumanId}`)
          try {
            const apiData = await this.loadDigitalHumanFromAPI(realDigitalHumanId)
            const digitalHuman = this.digitalHumans[realDigitalHumanId]
            if (apiData && digitalHuman) {
              // 🔒 保护：流式回复进行中或生图进行中时，不用服务端数据覆盖本地（避免正在打字/生图的气泡被清空）
              if (
                digitalHuman.isTyping ||
                digitalHuman.hasActiveImageTask ||
                (this.pendingReply && this.pendingReply.digitalHumanId === realDigitalHumanId)
              ) {
                console.log('⏸ 跳过合并：当前AI正在回复或生图中，避免覆盖本地流式/生图内容')
                return
              }
              console.log(`📬 更新聊天记录: ${realDigitalHumanId}`)

              // 合并策略：保留本地进行中的AI消息（isTyping 或带 msgId）
              const existingSessions = digitalHuman.sessions || []
              const incomingSessions = apiData.sessions || []

              const mergedSessions = [...incomingSessions]
              for (const localSession of existingSessions) {
                const targetIndex = mergedSessions.findIndex(s => s.id === localSession.id)
                if (targetIndex >= 0) {
                  const serverSession = mergedSessions[targetIndex]
                  // 🔧 覆盖合并：为同一条消息保留本地的翻译状态与译文
                  try {
                    const norm = (s: any) =>
                      typeof s === 'string' ? s.replace(/\s+/g, ' ').trim() : s
                    const byId = new Map((localSession.messages || []).map((m: any) => [m.id, m]))
                    const byKey = new Map(
                      (localSession.messages || []).map((m: any) => [
                        `${m.type}::${norm(m.content)}`,
                        m
                      ])
                    )
                    serverSession.messages = (serverSession.messages || []).map((m: any) => {
                      const lm: any = byId.get(m.id) || byKey.get(`${m.type}::${norm(m.content)}`)
                      if (lm && (lm.translated || lm.translatedContent || lm.translatedLang)) {
                        return {
                          ...m,
                          translated: lm.translated === true,
                          translatedContent: lm.translatedContent || m.translatedContent,
                          translatedLang: lm.translatedLang || m.translatedLang
                        }
                      }
                      return m
                    })
                  } catch (_) { }
                  const serverIds = new Set((serverSession.messages || []).map((m: any) => m.id))
                  // 🔧 修复：保留流式消息和本地发送的用户消息（防止进入页面后立即发消息导致消息消失）
                  const streamingLocals = (localSession.messages || []).filter(
                    (m: any) => m?.isTyping || m?.msgId
                  )
                  // 保留本地发送的用户消息（不在服务器消息列表中的）
                  const localUserMessages = (localSession.messages || []).filter(
                    (m: any) => m?.type === 'user' && !serverIds.has(m.id)
                  )
                  // 合并两者的去重结果
                  const allExtraLocals = [...streamingLocals, ...localUserMessages].filter(
                    (m: any, index, self) =>
                      index === self.findIndex((t: any) => t.id === m.id)
                  )
                  if (allExtraLocals.length > 0) {
                    serverSession.messages = [...(serverSession.messages || []), ...allExtraLocals]
                    serverSession.updatedAt = Date.now()
                  }
                } else {
                  // 尝试将本地翻译状态覆盖到服务器会话（即使会话ID不同）
                  try {
                    const serverSession0 = mergedSessions[0]
                    if (serverSession0) {
                      const localMsgById = new Map(
                        (localSession.messages || []).map((m: any) => [m.id, m])
                      )
                      const localMsgByContentType = new Map(
                        (localSession.messages || []).map((m: any) => [
                          `${m.type}::${m.content}`,
                          m
                        ])
                      )
                      serverSession0.messages = (serverSession0.messages || []).map((m: any) => {
                        let lm: any = localMsgById.get(m.id)
                        if (!lm) lm = localMsgByContentType.get(`${m.type}::${m.content}`)
                        if (lm && (lm.translated || lm.translatedContent || lm.translatedLang)) {
                          return {
                            ...m,
                            translated: lm.translated === true,
                            translatedContent: lm.translatedContent || m.translatedContent,
                            translatedLang: lm.translatedLang || m.translatedLang
                          }
                        }
                        return m
                      })
                    }
                  } catch (_) { }

                  // 兼容策略：仅在"进行中/被等待引用"的本地会话才临时保留
                  const hasStreamingLocal = (localSession.messages || []).some(
                    (m: any) => m?.isTyping || m?.msgId
                  )
                  const isPendingForThis =
                    !!this.pendingReply &&
                    this.pendingReply.digitalHumanId === realDigitalHumanId &&
                    this.pendingReply.sessionId === localSession.id
                  const hasWaitingForThis = Object.values(this.waitingReplies || {}).some(
                    (w: any) =>
                      w.digitalHumanId === realDigitalHumanId && w.sessionId === localSession.id
                  )
                  if (hasStreamingLocal || isPendingForThis || hasWaitingForThis) {
                    mergedSessions.push(localSession)
                  }
                }
              }

              digitalHuman.sessions = mergedSessions

              // 🔧 额外保护：在将服务器会话写入 store 之前，从 IndexedDB 中叠加已存在的译文（避免进入页面后再次异步覆盖）
              try {
                await chatRecordsDB.init()
                for (const session of digitalHuman.sessions || []) {
                  try {
                    const localMsgs: any[] = await chatRecordsDB.getMessagesBySession(session.id)
                    if (!Array.isArray(localMsgs) || localMsgs.length === 0) continue
                    const byId = new Map(localMsgs.map((m: any) => [m.id, m]))
                    session.messages = (session.messages || []).map((m: any) => {
                      const lm = byId.get(m.id)
                      if (lm && (lm.translated || lm.translatedContent || lm.translatedLang)) {
                        return {
                          ...m,
                          translated: lm.translated === true,
                          translatedContent: lm.translatedContent || m.translatedContent,
                          translatedLang: lm.translatedLang || m.translatedLang
                        }
                      }
                      return m
                    })
                  } catch (_) { }
                }
              } catch (e) {
                // ignore DB overlay errors,继续流程
              }
              // 当前会话优先级：服务端 -> 既有 -> pending/waiting -> 第一条
              const pendingSessionId =
                this.pendingReply?.digitalHumanId === realDigitalHumanId
                  ? this.pendingReply?.sessionId || null
                  : null
              const waitingSessionId = (() => {
                const item = Object.values(this.waitingReplies || {}).find(
                  (w: any) => w.digitalHumanId === realDigitalHumanId
                ) as any
                return item ? item.sessionId : null
              })()
              digitalHuman.currentSessionId =
                apiData.currentSessionId ||
                digitalHuman.currentSessionId ||
                pendingSessionId ||
                waitingSessionId ||
                mergedSessions[0]?.id ||
                null
              digitalHuman.lastSyncTime = Date.now()
              digitalHuman.isFromAPI = true

              // 🔧 优化：合并所有会话的消息到一个"全部记录"会话（首次加载时）
              this.mergeAllSessionMessages(realDigitalHumanId)

              this.saveToStorage()

              // 覆盖 API 数据中的每条消息译文
              await this.overlayTranslationsFromIndexedDB(realDigitalHumanId)
            }
          } catch (error) {
            console.error(`❌ 后台加载聊天记录失败: ${realDigitalHumanId}`, error)
          } finally {
            // 🔧 加载完成，清除初始化状态
            this.isInitializing = false
          }
        }, 100)
      } else {
        // 🔧 如果数字人已存在，每次切换都后台增量同步最新消息
        setTimeout(async () => {
          console.log('🔄 开始后台增量同步...')
          await this.incrementalSyncMessages(realDigitalHumanId)
        }, 100)
      }

      // 2. 切换到该数字人
      if (this.digitalHumans[realDigitalHumanId]) {
        // 🔧 修复：在切换前先保存之前的数字人ID
        const previousDigitalHumanId = this.currentDigitalHumanId

        // 🔧 新增：如果传入了新的头像或名称信息，更新现有数字人的信息
        const existingDigitalHuman = this.digitalHumans[realDigitalHumanId]
        if (name && existingDigitalHuman && existingDigitalHuman.name !== name) {
          existingDigitalHuman.name = name
          console.log(`🔄 更新数字人 ${realDigitalHumanId} 的名称: ${name}`)
        }
        if (avatar && existingDigitalHuman && existingDigitalHuman.avatar !== avatar) {
          existingDigitalHuman.avatar = avatar
          console.log(`🔄 更新数字人 ${realDigitalHumanId} 的头像: ${avatar}`)
        }

        // 🔧 修复：切换数字人时，智能清除过期状态
        // 避免从 IndexedDB 恢复的过期状态导致打字动画闪现
        if (previousDigitalHumanId !== realDigitalHumanId) {
          const newDigitalHuman = this.digitalHumans[realDigitalHumanId]
          if (newDigitalHuman) {
            // 🔧 关键修复：检查消息状态，智能判断是否应该清除 loading/typing
            // 异步执行，不阻塞切换流程
            this.checkAndClearExpiredStates(realDigitalHumanId)
          }
        }

        this.currentDigitalHumanId = realDigitalHumanId

        // 🔧 新逻辑：检查是否有等待回复的消息，如果有则标记为未读
        const waitingRepliesForOtherHumans = Object.values(this.waitingReplies).filter(
          waiting => waiting.digitalHumanId !== realDigitalHumanId
        )

        waitingRepliesForOtherHumans.forEach(waiting => {
          this.incrementUnreadCount(waiting.digitalHumanId)
        })

        // 🔧 智能处理：保留 pendingReply 状态以确保消息正确路由
        if (this.pendingReply && this.pendingReply.digitalHumanId !== realDigitalHumanId) {
          console.log(
            `🔄 切换AI：${this.pendingReply.digitalHumanId} -> ${realDigitalHumanId}，保留待回复状态确保消息路由正确`
          )
        }

        // 🔧 清除切换前的AI的无关状态（但保留等待回复的状态）
        if (previousDigitalHumanId && previousDigitalHumanId !== realDigitalHumanId) {
          // 清除之前AI的loading状态，但保留等待回复的typing状态
          const previousDigitalHuman = this.digitalHumans[previousDigitalHumanId]
          if (previousDigitalHuman && !this.hasWaitingReplies(previousDigitalHumanId)) {
            // 只有在没有等待回复时才清除状态
            this.clearDigitalHumanStates(previousDigitalHumanId, {
              loading: false,
              typing: false
            })
            console.log(`🔄 清除之前AI(${previousDigitalHumanId})的加载状态`)
          }
        }

        // 🔧 新增：标记当前数字人为已读
        this.markCurrentAsRead()

        // 🔧 优化：合并所有会话的消息到一个"全部记录"会话
        this.mergeAllSessionMessages(realDigitalHumanId)

        this.saveToStorage()
      } else {
      }
    },

    /**
     * 刷新数字人数据（强制从API重新加载）
     */
    async refreshDigitalHuman(id: string) {
      const apiData = await this.loadDigitalHumanFromAPI(id)

      if (apiData && this.digitalHumans[id]) {
        // 更新数字人信息，但保留本地的临时状态
        const local = this.digitalHumans[id]
        const incoming = apiData.sessions || []
        const merged: ChatSession[] = [...incoming]

        try {
          for (const localSession of local.sessions || []) {
            const targetIndex = merged.findIndex(s => s.id === localSession.id)
            if (targetIndex >= 0) {
              const serverSession = merged[targetIndex]
              const localMsgMap = new Map((localSession.messages || []).map((m: any) => [m.id, m]))
              const serverIds = new Set((serverSession.messages || []).map((m: any) => m.id))
              serverSession.messages = (serverSession.messages || []).map((m: any) => {
                const lm: any = localMsgMap.get(m.id)
                if (lm && (lm.translated || lm.translatedContent || lm.translatedLang)) {
                  return {
                    ...m,
                    translated: lm.translated === true,
                    translatedContent: lm.translatedContent || m.translatedContent,
                    translatedLang: lm.translatedLang || m.translatedLang
                  }
                }
                return m
              })
              // 🔧 修复：保留本地发送的用户消息（不在服务器消息列表中的）
              const localUserMessages = (localSession.messages || []).filter(
                (m: any) => m?.type === 'user' && !serverIds.has(m.id)
              )
              if (localUserMessages.length > 0) {
                serverSession.messages = [...(serverSession.messages || []), ...localUserMessages]
                serverSession.updatedAt = Date.now()
              }
            }
          }
        } catch (_) { }

        this.digitalHumans[id] = {
          ...local,
          name: apiData.name || local.name,
          avatar: apiData.avatar || local.avatar,
          sessions: merged,
          currentSessionId: apiData.currentSessionId || local.currentSessionId,
          lastSyncTime: Date.now(),
          isFromAPI: true
        }

        this.saveToStorage()
      } else {
      }
    },

    /**
     * 生成唯一ID
     */
    generateId(): string {
      return Date.now().toString(36) + Math.random().toString(36).substr(2)
    },

    /**
     * 创建新会话
     */
    createSession(name?: string): ChatSession | null {
      const current = this.currentDigitalHuman
      if (!current) return null

      const session: ChatSession = {
        id: this.generateId(),
        name: name || `Chat ${current.sessions.length + 1}`,
        messages: [],
        createdAt: Date.now(),
        updatedAt: Date.now(),
        avatar: current.avatar
      }

      current.sessions.unshift(session)
      current.currentSessionId = session.id
      this.saveToStorage()
      return session
    },

    /**
     * 切换会话
     */
    switchSession(sessionId: string) {
      const current = this.currentDigitalHuman
      if (!current) return

      const session = current.sessions.find(s => s.id === sessionId)
      if (session) {
        current.currentSessionId = sessionId
      }
    },

    /**
     * 删除会话
     */
    deleteSession(sessionId: string) {
      const current = this.currentDigitalHuman
      if (!current) return

      const index = current.sessions.findIndex(s => s.id === sessionId)
      if (index > -1) {
        current.sessions.splice(index, 1)

        // 如果删除的是当前会话，切换到其他会话
        if (current.currentSessionId === sessionId) {
          if (current.sessions.length > 0) {
            current.currentSessionId = current.sessions[0]?.id || null
          } else {
            // 如果没有会话了，创建新会话
            this.createSession()
          }
        }
      }
    },

    /**
     * 删除数字人（包括所有聊天记录）
     */
    deleteDigitalHuman(digitalHumanId: string) {
      // 删除数字人数据
      if (this.digitalHumans[digitalHumanId]) {
        delete this.digitalHumans[digitalHumanId]
      }

      // 如果删除的是当前数字人，重置状态
      if (this.currentDigitalHumanId === digitalHumanId) {
        this.currentDigitalHumanId = null
      }

      // 保存到localStorage
      this.saveToStorage()
    },

    /**
     * 添加消息
     */
    addMessage(message: Omit<ChatMessage, 'id' | 'timestamp'>): ChatMessage | null {
      // console.log(message, 'message-------message')

      const session = this.currentSession
      if (!session) return null

      // 🔧 获取当前数字人的全局翻译状态
      const isGlobalTranslationEnabled =
        this.currentDigitalHumanId && this.digitalHumans[this.currentDigitalHumanId]
          ? this.digitalHumans[this.currentDigitalHumanId].isGlobalTranslationEnabled || false
          : false

      const newMessage: ChatMessage = {
        id: this.generateId(),
        timestamp: Date.now(),
        content: message.content,
        type: message.type,
        status: message.status,
        avatar: message.avatar,
        username: message.username,
        isTyping: message.isTyping,
        msgId: message.msgId,
        isLoadingTranslate: (message as any).isLoadingTranslate,
        generatedImageUrl: (message as any).generatedImageUrl,
        // 🔧 设置全局翻译标记
        globalTranslation: isGlobalTranslationEnabled
      }

      session.messages.push(newMessage)
      session.updatedAt = Date.now()
      session.lastMessage = message.content

      // 🔧 性能优化：更新消息ID缓存（仅对AI消息）
      if (newMessage.type === 'ai' && newMessage.msgId && this.currentDigitalHumanId) {
        this._messageIdCache.set(newMessage.msgId, {
          digitalHumanId: this.currentDigitalHumanId,
          sessionId: session.id,
          messageId: newMessage.id
        })
      }

      // 🔧 性能优化：标记当前数字人数据已变更
      if (this.currentDigitalHumanId) {
        this.markDigitalHumanDirty(this.currentDigitalHumanId)
      }

      this.saveToStorage()

      return newMessage
    },

    /**
     * 更新消息状态
     */
    updateMessageStatus(messageId: string, status: MessageStatus) {
      const session = this.currentSession
      if (!session) return

      const message = session.messages.find((m: any) => m.id === messageId)
      if (message) {
        message.status = status
      }
    },

    /**
     * 更新消息内容（用于打字机效果和流式消息）
     */
    updateMessageContent(messageId: string, content: string, isTyping?: boolean) {
      const session = this.currentSession
      if (!session) return

      const message = session.messages.find((m: any) => m.id === messageId)
      if (message) {
        // 直接设置内容，因为后端已经处理了累积
        message.content = content

        if (isTyping !== undefined) {
          message.isTyping = isTyping
        }

        // 更新会话的最后更新时间
        session.updatedAt = Date.now()
      }
    },

    /**
     * 删除单条消息
     */
    deleteMessage(messageId: string) {
      const session = this.currentSession
      if (!session) return

      const messageIndex = session.messages.findIndex((m: any) => m.id === messageId)
      if (messageIndex > -1) {
        // 🔧 性能优化：删除消息前清理缓存
        const deletedMessage = session.messages[messageIndex]
        if (deletedMessage.type === 'ai' && deletedMessage.msgId) {
          this._messageIdCache.delete(deletedMessage.msgId)
        }

        // 删除消息
        session.messages.splice(messageIndex, 1)

        // 更新会话的最后更新时间
        session.updatedAt = Date.now()

        // 如果删除后没有消息了，清空最后消息
        if (session.messages.length === 0) {
          session.lastMessage = undefined
        } else {
          // 更新最后一条消息
          const lastMessage = session.messages[session.messages.length - 1]
          session.lastMessage = lastMessage?.content
        }

        // 保存到localStorage
        this.saveToStorage()
      }
    },

    /**
     * 清空当前会话消息
     */
    clearCurrentSessionMessages() {
      const session = this.currentSession
      if (session) {
        session.messages = []
        session.lastMessage = undefined
        session.updatedAt = Date.now()

        // 清除等待回复状态，避免再次进入时自动发送消息
        this.clearPendingReply()

        // 清除当前数字人的所有等待回复记录
        if (this.currentDigitalHumanId && session.id) {
          const waitingReplies = this.getWaitingReplies(this.currentDigitalHumanId)
          waitingReplies.forEach(waiting => {
            if (waiting.sessionId === session.id) {
              this.removeWaitingReply(
                this.currentDigitalHumanId!,
                waiting.sessionId,
                waiting.userMessageId
              )
            }
          })
        }

        // 清除加载和打字状态
        if (this.currentDigitalHumanId) {
          this.clearDigitalHumanStates(this.currentDigitalHumanId, {
            loading: false,
            typing: false
          })
        }

        this.saveToStorage()
      }
    },

    /**
     * 设置加载状态（针对当前数字人）
     */
    setLoading(loading: boolean) {
      const current = this.currentDigitalHuman
      if (current) {
        current.isLoading = loading
        this.saveToStorage()
      }
    },

    /**
     * 设置打字状态（针对当前数字人）
     */
    setTyping(typing: boolean) {
      const current = this.currentDigitalHuman
      if (current) {
        current.isTyping = typing
        this.saveToStorage()
      }
    },

    /**
     * 设置失败状态
     */
    setIsFailed(isFailed: boolean) {
      this.isFailed = isFailed
    },

    /**
     * 设置当前输入消息
     */
    setCurrentMessage(message: string) {
      this.currentMessage = message
    },
    /**
     * 快捷回复消息
    */
    async sendQuickReplyMessage(quickReply?: any, roleId?: string): Promise<void> {
      if (this.isLoading) return

      // 确保有当前数字人和会话
      if (!this.currentDigitalHuman) return

      // 🔧 修复：不自动创建会话，而是使用现有会话或创建临时会话
      // 会话 ID 将由后端在首次回复时提供
      let currentSessionId = this.currentSession?.id

      if (!this.currentSession) {
        // 如果没有会话，创建一个临时会话
        const tempSession = this.createSession()
        currentSessionId = tempSession?.id
      }

      // 仅在 AI 正在回复中时禁止继续发送；其他情况允许
      if (this.isTyping) return

      // 确定要使用的roleId
      const actualRoleId =
        this.currentDigitalHumanId === 'default' ? '378' : this.currentDigitalHumanId
      const targetRoleId = roleId || actualRoleId || '378'

      // 添加用户消息
      const userMessage = this.addMessage({
        content: quickReply.title,
        type: 'user' as MessageType,
        status: 'sending' as MessageStatus,
        username: 'You'
      })

      if (!userMessage) return

      // 标记用户已经聊过天（用于判断是否是第一次聊天）
      localStorage.setItem('user_has_chatted_before', 'true')

      // 清空输入框
      this.setCurrentMessage('')

      // 🔧 修复：设置等待回复状态，使用临时标记而不是具体的 sessionId
      // 这样即使 sessionId 不匹配，也能找到对应的会话
      if (this.currentDigitalHumanId) {
        // 使用特殊标记表示等待回复，而不依赖具体的 sessionId
        this.setPendingReply(this.currentDigitalHumanId, currentSessionId || 'temp')
      }

      // 🔧 新增：添加等待回复状态（用于未读消息管理）
      if (this.currentDigitalHumanId && currentSessionId) {
        this.addWaitingReply(this.currentDigitalHumanId, currentSessionId, userMessage.id)
        console.log('📤 添加 waitingReply:', {
          digitalHumanId: this.currentDigitalHumanId,
          sessionId: currentSessionId,
          userMessageId: userMessage.id
        })
      }

      // 🔧 修复：设置当前显示的数字人的加载状态，而不是目标AI
      // 这样用户在当前页面能看到加载动画
      if (this.currentDigitalHumanId) {
        this.clearDigitalHumanStates(this.currentDigitalHumanId, {
          loading: true,
          typing: false // 发送阶段显示loading，不显示typing
        })
      }

      try {
        // 🔧 优化：发送消息直接使用 HTTP API
        // SSE 只负责接收 AI 的流式响应，不负责发送消息

        // 更新用户消息状态为已发送
        this.updateMessageStatus(userMessage.id, 'sent' as MessageStatus)

        // 调用HTTP接口发送消息（不等待回复）
        try {
          const { shortLang } = useLanguage()
          const langParam = this.autoTranslate ? shortLang.value : ''
          const sendMessageAPI = await MessageAPI.sendMessage({
            content: "quick-reply",
            roleId: targetRoleId,
            language: langParam,
            autoTranslate: this.autoTranslate ? 1 : 0,
            isContinue: !!quickReply,
            quickReplyId: quickReply?.id,
            // notStream: true
          })
          const apiRes = await sendMessageAPI

          // ✅ 接口请求成功后，启动无回复埋点定时器
          sendBurialPoint.startNoReplyTrackingTimer40s()
          sendBurialPoint.startNoReplyTrackingTimer30s()
          sendBurialPoint.startNoReplyTrackingTimer20s()
          sendBurialPoint.startNoReplyTrackingTimer15s()
          sendBurialPoint.startNoReplyTrackingTimer10s()
          // 🔧 新增：如果后端返回了真实的消息ID，用它覆盖本地这条用户消息的临时ID
          // 这样后续从历史接口拉回来的同一条消息（按 id）就能对上，不会出现重复的用户消息
          try {
            const realId = apiRes?.data?.userMsg?.msgId
            localStorage.setItem('user_msg_id', String(apiRes?.data?.msgId))
            this.latestSentMessageId = String(realId)
            if (realId && userMessage) {
              // 🔧 修复：只更新 id，确保不覆盖 type 字段
              ; (userMessage as any).id = String(realId)
              // 🔧 确保消息类型始终是 'user'，防止后端数据覆盖
              if (userMessage.type !== 'user') {
                console.warn('⚠️ 用户消息类型异常，已修复:', userMessage.type, '-> user')
                  ; (userMessage as any).type = 'user'
              }
            }
          } catch (_) {
            // 如果解析失败，不影响主流程
          }

          // 🔧 关键修复：HTTP 成功后检查连接是否仍然正常
          // 根据 modelIndex 判断使用 SSE 还是 SSE 接收 AI 回复
          // 需要给用户提示网络问题
          let isStillConnected: boolean

          const sseManager = getSSEManager()
          isStillConnected = sseManager.isReallyConnected()

          if (!isStillConnected) {
            console.log(`⚠️ SSE 已断开，无法接收 AI 回复`)

            // 清理加载状态
            this.clearDigitalHumanStates(targetRoleId, {
              loading: false,
              typing: false
            })

            // 显示网络不稳定提示
            this.addMessage({
              content:
                'The network connection is unstable. The message has been sent, but the reply may be delayed. Please check your network and refresh the page.',
              type: 'system' as MessageType,
              status: 'received' as MessageStatus,
              username: 'System'
            })

            return
          }

          // 🔧 设置30秒超时，超时后开始轮询拉取单条消息记录
          let pollingTimerId: NodeJS.Timeout | null = null
          let retryCount = 0
          const MAX_RETRY_COUNT = 30 // 最多重试30次（总共60秒）

          this.currentTimeoutId = setTimeout(
            async () => {
              const userMsgId = apiRes?.data?.msgId
              if (!userMsgId) {
                console.warn('⚠️ 没有用户消息 ID，无法拉取')
                this.clearDigitalHumanStates(targetRoleId, {
                  loading: false,
                  typing: false
                })
                this.currentTimeoutId = null
                return
              }

              console.log('⏰ SSE 超时30秒，检查是否需要轮询拉取单条消息记录')

              // 🔧 在轮询前检查：SSE 可能已经收到了 AI 回复
              const targetDigitalHuman = this.digitalHumans[targetRoleId]
              const currentSession = targetDigitalHuman?.sessions?.find(
                s => s.id === targetDigitalHuman.currentSessionId
              )

              // 检查会话中最后一条消息是否是 AI 回复，且时间戳在用户消息之后
              const lastMessage = currentSession?.messages?.[currentSession.messages.length - 1]
              if (lastMessage?.type === 'ai' && lastMessage.timestamp > userMessage.timestamp) {
                console.log('✅ SSE 已经收到 AI 回复，无需轮询')
                this.clearDigitalHumanStates(targetRoleId, {
                  loading: false,
                  typing: false
                })
                this.currentTimeoutId = null
                return
              }

              console.log('🔄 开始轮询拉取单条消息记录')

              // 轮询函数：每2秒尝试一次，直到成功或达到最大重试次数
              const pollMessage = async () => {
                const targetDigitalHuman = this.digitalHumans[targetRoleId]

                // 如果状态已经不是加载中，说明已经收到消息或用户取消了，停止轮询
                if (
                  !targetDigitalHuman ||
                  (!targetDigitalHuman.isLoading && !targetDigitalHuman.isTyping)
                ) {
                  if (pollingTimerId) {
                    clearInterval(pollingTimerId)
                    pollingTimerId = null
                  }
                  return
                }

                // 🔧 每次轮询前检查：SSE 是否已经收到了消息
                const currentSession = targetDigitalHuman?.sessions?.find(
                  s => s.id === targetDigitalHuman.currentSessionId
                )
                const lastMessage =
                  currentSession?.messages?.[currentSession.messages.length - 1]
                if (
                  lastMessage?.type === 'ai' &&
                  lastMessage.timestamp > userMessage.timestamp
                ) {
                  console.log('✅ SSE 已收到消息，停止轮询')
                  if (pollingTimerId) {
                    clearInterval(pollingTimerId)
                    pollingTimerId = null
                  }
                  this.clearDigitalHumanStates(targetRoleId, {
                    loading: false,
                    typing: false
                  })
                  return
                }

                retryCount++
                console.log(`🔄 第 ${retryCount} 次尝试拉取消息...`)

                try {
                  const chatMsgRes = await MessageAPI.getChatMsg({
                    msgId: userMsgId
                  })

                  const msgData = chatMsgRes?.data
                  const aiReply = msgData?.content
                  const messageRole = msgData?.role

                  // 🔧 修复：只处理 AI 回复消息，跳过用户消息
                  if (aiReply && messageRole !== 'user') {
                    console.log('✅ 成功拉取到 AI 回复')

                    sendBurialPoint.longReplyFetchSuccess()
                    // ✅ 收到AI回复，清除所有无回复埋点定时器
                    sendBurialPoint.clearNoReplyTrackingTimer()

                    // 停止轮询
                    if (pollingTimerId) {
                      clearInterval(pollingTimerId)
                      pollingTimerId = null
                    }

                    // 添加 AI 回复消息
                    const aiMessage = this.addMessage({
                      content: aiReply,
                      type: 'ai' as MessageType,
                      status: 'received' as MessageStatus,
                      username: this.currentDigitalHuman?.name || 'AI',
                      avatar: this.currentDigitalHuman?.avatar || ''
                    })

                    // 设置消息 ID
                    if (aiMessage && msgData?.msgId) {
                      ; (aiMessage as any).id = String(msgData.msgId)
                    }

                    // 清理加载状态
                    this.clearDigitalHumanStates(targetRoleId, {
                      loading: false,
                      typing: false
                    })
                  } else if (messageRole === 'user') {
                    // 如果返回的是用户消息，继续等待 AI 回复
                    console.log('⏳ 拉取到用户消息，继续等待 AI 回复...')
                  } else if (retryCount >= MAX_RETRY_COUNT) {
                    // 拉取超过60秒都没有消息
                    sendBurialPoint.noMessagePulled()
                    // 达到最大重试次数，停止轮询
                    console.warn('⚠️ 达到最大重试次数，停止轮询')
                    if (pollingTimerId) {
                      clearInterval(pollingTimerId)
                      pollingTimerId = null
                    }

                    // 显示超时提示
                    const randomKey =
                      timeoutPromptWord[Math.floor(Math.random() * timeoutPromptWord.length)]

                    this.addMessage({
                      content: this.getTranslation(randomKey || I18N_KEYS.SSE_TIMEOUT),
                      type: 'system' as MessageType,
                      status: 'received' as MessageStatus,
                      username: 'System'
                    })

                    this.clearDigitalHumanStates(targetRoleId, {
                      loading: false,
                      typing: false
                    })
                  } else {
                    console.log('⏳ 未获取到数据，2秒后重试...')
                  }
                } catch (error) {
                  sendBurialPoint.longReplyFetchFailed()
                  console.error(`❌ 第 ${retryCount} 次拉取失败:`, error)

                  if (retryCount >= MAX_RETRY_COUNT) {
                    // 达到最大重试次数
                    if (pollingTimerId) {
                      clearInterval(pollingTimerId)
                      pollingTimerId = null
                    }

                    const randomKey =
                      timeoutPromptWord[Math.floor(Math.random() * timeoutPromptWord.length)]

                    this.addMessage({
                      content: this.getTranslation(randomKey || I18N_KEYS.SSE_TIMEOUT),
                      type: 'system' as MessageType,
                      status: 'received' as MessageStatus,
                      username: 'System'
                    })

                    this.clearDigitalHumanStates(targetRoleId, {
                      loading: false,
                      typing: false
                    })
                  }
                }
              }

              // 立即执行第一次
              await pollMessage()

              // 如果第一次没有成功，启动定时轮询（每2秒一次）
              if (!pollingTimerId && retryCount < MAX_RETRY_COUNT) {
                pollingTimerId = setInterval(pollMessage, 2000)
              }

              // 清除超时定时器ID
              this.currentTimeoutId = null
            },
            localStorage.getItem('modelIndex') === '3' ? 15000 : SSE_TIMEOUT_MS
          )

          // 不在这里处理回复，等待SSE消息
          return
        } catch (apiError) {
          // HTTP接口调用失败，发送失败埋点
          sendBurialPoint.sendMessageFailed()

          // 更新用户消息状态为失败
          this.updateMessageStatus(userMessage.id, 'failed' as MessageStatus)

          // HTTP发送失败，添加错误消息
          this.addMessage({
            content: 'Send failed',
            type: 'system' as MessageType,
            status: 'received' as MessageStatus,
            username: 'System'
          })

          // 🔧 优化：清理目标数字人的状态
          this.clearDigitalHumanStates(targetRoleId, {
            loading: false,
            typing: false
          })
          return
        }
      } catch (error) {
        // 移除打字占位消息
        const session = this.currentSession
        if (session) {
          const typingIndex = session.messages.findIndex(m => m.isTyping)
          if (typingIndex > -1) {
            session.messages.splice(typingIndex, 1)
          }
        }

        // 更新用户消息状态为失败
        this.updateMessageStatus(userMessage.id, 'failed' as MessageStatus)

        // 失败兜底：清理等待与状态，防止出现在其他角色页面
        if (this.currentDigitalHumanId && this.currentSession?.id) {
          this.removeWaitingReply(
            this.currentDigitalHumanId,
            this.currentSession.id,
            userMessage.id
          )
        }
        this.clearPendingReply()
        if (this.currentDigitalHumanId) {
          this.clearDigitalHumanStates(this.currentDigitalHumanId, {
            loading: false,
            typing: false
          })
        }

        // 添加错误消息
        this.addMessage({
          content: 'Send failed',
          type: 'system' as MessageType,
          status: 'received' as MessageStatus,
          username: 'System'
        })
      } finally {
        // this.setLoading(false);
        this.setTyping(false)
      }
    },
    /**
     * 发送消息（优先使用SSE）
     */
    async sendMessage(content: string, roleId?: string, quickReply?: any): Promise<void> {
      if (!content.trim() || this.isLoading) return

      // 确保有当前数字人和会话
      if (!this.currentDigitalHuman) return

      // 🔧 修复：不自动创建会话，而是使用现有会话或创建临时会话
      // 会话 ID 将由后端在首次回复时提供
      let currentSessionId = this.currentSession?.id

      if (!this.currentSession) {
        // 如果没有会话，创建一个临时会话
        const tempSession = this.createSession()
        currentSessionId = tempSession?.id
      }

      // 仅在 AI 正在回复中时禁止继续发送；其他情况允许
      if (this.isTyping) return

      // 确定要使用的roleId
      const actualRoleId =
        this.currentDigitalHumanId === 'default' ? '378' : this.currentDigitalHumanId
      const targetRoleId = roleId || actualRoleId || '378'

      // 添加用户消息
      const userMessage = this.addMessage({
        content: content.trim(),
        type: 'user' as MessageType,
        status: 'sending' as MessageStatus,
        username: 'You'
      })

      if (!userMessage) return

      // 标记用户已经聊过天（用于判断是否是第一次聊天）
      localStorage.setItem('user_has_chatted_before', 'true')

      // 清空输入框
      this.setCurrentMessage('')

      // 🔧 修复：设置等待回复状态，使用临时标记而不是具体的 sessionId
      // 这样即使 sessionId 不匹配，也能找到对应的会话
      if (this.currentDigitalHumanId) {
        // 使用特殊标记表示等待回复，而不依赖具体的 sessionId
        this.setPendingReply(this.currentDigitalHumanId, currentSessionId || 'temp')
      }

      // 🔧 新增：添加等待回复状态（用于未读消息管理）
      if (this.currentDigitalHumanId && currentSessionId) {
        this.addWaitingReply(this.currentDigitalHumanId, currentSessionId, userMessage.id)
        console.log('📤 添加 waitingReply:', {
          digitalHumanId: this.currentDigitalHumanId,
          sessionId: currentSessionId,
          userMessageId: userMessage.id
        })
      }

      // 🔧 修复：设置当前显示的数字人的加载状态，而不是目标AI
      // 这样用户在当前页面能看到加载动画
      if (this.currentDigitalHumanId) {
        this.clearDigitalHumanStates(this.currentDigitalHumanId, {
          loading: true,
          typing: false // 发送阶段显示loading，不显示typing
        })
      }

      try {
        // 🔧 优化：发送消息直接使用 HTTP API
        // SSE 只负责接收 AI 的流式响应，不负责发送消息

        // 更新用户消息状态为已发送
        this.updateMessageStatus(userMessage.id, 'sent' as MessageStatus)

        // 调用HTTP接口发送消息（不等待回复）
        try {
          const { shortLang } = useLanguage()
          const langParam = this.autoTranslate ? shortLang.value : ''
          const sendMessageAPI = await MessageAPI.sendMessage({
            content: content.trim(),
            roleId: targetRoleId,
            language: langParam,
            autoTranslate: this.autoTranslate ? 1 : 0,
            isContinue: !!quickReply,
            quickReplyId: quickReply?.id,
            // notStream: true
          })
          const apiRes = await sendMessageAPI

          // ✅ 接口请求成功后，启动无回复埋点定时器
          sendBurialPoint.startNoReplyTrackingTimer40s()
          sendBurialPoint.startNoReplyTrackingTimer30s()
          sendBurialPoint.startNoReplyTrackingTimer20s()
          sendBurialPoint.startNoReplyTrackingTimer15s()
          sendBurialPoint.startNoReplyTrackingTimer10s()
          // 🔧 新增：如果后端返回了真实的消息ID，用它覆盖本地这条用户消息的临时ID
          // 这样后续从历史接口拉回来的同一条消息（按 id）就能对上，不会出现重复的用户消息
          try {
            const realId = apiRes?.data?.userMsg?.msgId
            localStorage.setItem('user_msg_id', String(apiRes?.data?.msgId))
            this.latestSentMessageId = String(realId)
            if (realId && userMessage) {
              // 🔧 修复：只更新 id，确保不覆盖 type 字段
              ; (userMessage as any).id = String(realId)
              // 🔧 确保消息类型始终是 'user'，防止后端数据覆盖
              if (userMessage.type !== 'user') {
                console.warn('⚠️ 用户消息类型异常，已修复:', userMessage.type, '-> user')
                  ; (userMessage as any).type = 'user'
              }
            }
          } catch (_) {
            // 如果解析失败，不影响主流程
          }

          // 🔧 关键修复：HTTP 成功后检查连接是否仍然正常
          // 根据 modelIndex 判断使用 SSE 还是 SSE 接收 AI 回复
          // 需要给用户提示网络问题
          let isStillConnected: boolean

          const sseManager = getSSEManager()
          isStillConnected = sseManager.isReallyConnected()

          if (!isStillConnected) {
            console.log(`⚠️ SSE 已断开，无法接收 AI 回复`)

            // 清理加载状态
            this.clearDigitalHumanStates(targetRoleId, {
              loading: false,
              typing: false
            })

            // 显示网络不稳定提示
            this.addMessage({
              content:
                'The network connection is unstable. The message has been sent, but the reply may be delayed. Please check your network and refresh the page.',
              type: 'system' as MessageType,
              status: 'received' as MessageStatus,
              username: 'System'
            })

            return
          }

          // 🔧 设置30秒超时，超时后开始轮询拉取单条消息记录
          let pollingTimerId: NodeJS.Timeout | null = null
          let retryCount = 0
          const MAX_RETRY_COUNT = 30 // 最多重试30次（总共60秒）

          this.currentTimeoutId = setTimeout(
            async () => {
              const userMsgId = apiRes?.data?.msgId
              if (!userMsgId) {
                console.warn('⚠️ 没有用户消息 ID，无法拉取')
                this.clearDigitalHumanStates(targetRoleId, {
                  loading: false,
                  typing: false
                })
                this.currentTimeoutId = null
                return
              }

              console.log('⏰ SSE 超时30秒，检查是否需要轮询拉取单条消息记录')

              // 🔧 在轮询前检查：SSE 可能已经收到了 AI 回复
              const targetDigitalHuman = this.digitalHumans[targetRoleId]
              const currentSession = targetDigitalHuman?.sessions?.find(
                s => s.id === targetDigitalHuman.currentSessionId
              )

              // 检查会话中最后一条消息是否是 AI 回复，且时间戳在用户消息之后
              const lastMessage = currentSession?.messages?.[currentSession.messages.length - 1]
              if (lastMessage?.type === 'ai' && lastMessage.timestamp > userMessage.timestamp) {
                console.log('✅ SSE 已经收到 AI 回复，无需轮询')
                this.clearDigitalHumanStates(targetRoleId, {
                  loading: false,
                  typing: false
                })
                this.currentTimeoutId = null
                return
              }

              console.log('🔄 开始轮询拉取单条消息记录')

              // 轮询函数：每2秒尝试一次，直到成功或达到最大重试次数
              const pollMessage = async () => {
                const targetDigitalHuman = this.digitalHumans[targetRoleId]

                // 如果状态已经不是加载中，说明已经收到消息或用户取消了，停止轮询
                if (
                  !targetDigitalHuman ||
                  (!targetDigitalHuman.isLoading && !targetDigitalHuman.isTyping)
                ) {
                  if (pollingTimerId) {
                    clearInterval(pollingTimerId)
                    pollingTimerId = null
                  }
                  return
                }

                // 🔧 每次轮询前检查：SSE 是否已经收到了消息
                const currentSession = targetDigitalHuman?.sessions?.find(
                  s => s.id === targetDigitalHuman.currentSessionId
                )
                const lastMessage =
                  currentSession?.messages?.[currentSession.messages.length - 1]
                if (
                  lastMessage?.type === 'ai' &&
                  lastMessage.timestamp > userMessage.timestamp
                ) {
                  console.log('✅ SSE 已收到消息，停止轮询')
                  if (pollingTimerId) {
                    clearInterval(pollingTimerId)
                    pollingTimerId = null
                  }
                  this.clearDigitalHumanStates(targetRoleId, {
                    loading: false,
                    typing: false
                  })
                  return
                }

                retryCount++
                console.log(`🔄 第 ${retryCount} 次尝试拉取消息...`)

                try {
                  const chatMsgRes = await MessageAPI.getChatMsg({
                    msgId: userMsgId
                  })

                  const msgData = chatMsgRes?.data
                  const aiReply = msgData?.content
                  const messageRole = msgData?.role

                  // 🔧 修复：只处理 AI 回复消息，跳过用户消息
                  if (aiReply && messageRole !== 'user') {
                    console.log('✅ 成功拉取到 AI 回复')

                    sendBurialPoint.longReplyFetchSuccess()
                    // ✅ 收到AI回复，清除所有无回复埋点定时器
                    sendBurialPoint.clearNoReplyTrackingTimer()

                    // 停止轮询
                    if (pollingTimerId) {
                      clearInterval(pollingTimerId)
                      pollingTimerId = null
                    }

                    // 添加 AI 回复消息
                    const aiMessage = this.addMessage({
                      content: aiReply,
                      type: 'ai' as MessageType,
                      status: 'received' as MessageStatus,
                      username: this.currentDigitalHuman?.name || 'AI',
                      avatar: this.currentDigitalHuman?.avatar || ''
                    })

                    // 设置消息 ID
                    if (aiMessage && msgData?.msgId) {
                      ; (aiMessage as any).id = String(msgData.msgId)
                    }

                    // 清理加载状态
                    this.clearDigitalHumanStates(targetRoleId, {
                      loading: false,
                      typing: false
                    })
                  } else if (messageRole === 'user') {
                    // 如果返回的是用户消息，继续等待 AI 回复
                    console.log('⏳ 拉取到用户消息，继续等待 AI 回复...')
                  } else if (retryCount >= MAX_RETRY_COUNT) {
                    // 拉取超过60秒都没有消息
                    sendBurialPoint.noMessagePulled()
                    // 达到最大重试次数，停止轮询
                    console.warn('⚠️ 达到最大重试次数，停止轮询')
                    if (pollingTimerId) {
                      clearInterval(pollingTimerId)
                      pollingTimerId = null
                    }

                    // 显示超时提示
                    const randomKey =
                      timeoutPromptWord[Math.floor(Math.random() * timeoutPromptWord.length)]

                    this.addMessage({
                      content: this.getTranslation(randomKey || I18N_KEYS.SSE_TIMEOUT),
                      type: 'system' as MessageType,
                      status: 'received' as MessageStatus,
                      username: 'System'
                    })

                    this.clearDigitalHumanStates(targetRoleId, {
                      loading: false,
                      typing: false
                    })
                  } else {
                    console.log('⏳ 未获取到数据，2秒后重试...')
                  }
                } catch (error) {
                  sendBurialPoint.longReplyFetchFailed()
                  console.error(`❌ 第 ${retryCount} 次拉取失败:`, error)

                  if (retryCount >= MAX_RETRY_COUNT) {
                    // 达到最大重试次数
                    if (pollingTimerId) {
                      clearInterval(pollingTimerId)
                      pollingTimerId = null
                    }

                    const randomKey =
                      timeoutPromptWord[Math.floor(Math.random() * timeoutPromptWord.length)]

                    this.addMessage({
                      content: this.getTranslation(randomKey || I18N_KEYS.SSE_TIMEOUT),
                      type: 'system' as MessageType,
                      status: 'received' as MessageStatus,
                      username: 'System'
                    })

                    this.clearDigitalHumanStates(targetRoleId, {
                      loading: false,
                      typing: false
                    })
                  }
                }
              }

              // 立即执行第一次
              await pollMessage()

              // 如果第一次没有成功，启动定时轮询（每2秒一次）
              if (!pollingTimerId && retryCount < MAX_RETRY_COUNT) {
                pollingTimerId = setInterval(pollMessage, 2000)
              }

              // 清除超时定时器ID
              this.currentTimeoutId = null
            },
            localStorage.getItem('modelIndex') === '3' ? 15000 : SSE_TIMEOUT_MS
          )

          // 不在这里处理回复，等待SSE消息
          return
        } catch (apiError) {
          // HTTP接口调用失败，发送失败埋点
          sendBurialPoint.sendMessageFailed()

          // 更新用户消息状态为失败
          this.updateMessageStatus(userMessage.id, 'failed' as MessageStatus)

          // HTTP发送失败，添加错误消息
          this.addMessage({
            content: 'Send failed',
            type: 'system' as MessageType,
            status: 'received' as MessageStatus,
            username: 'System'
          })

          // 🔧 优化：清理目标数字人的状态
          this.clearDigitalHumanStates(targetRoleId, {
            loading: false,
            typing: false
          })
          return
        }
      } catch (error) {
        // 移除打字占位消息
        const session = this.currentSession
        if (session) {
          const typingIndex = session.messages.findIndex(m => m.isTyping)
          if (typingIndex > -1) {
            session.messages.splice(typingIndex, 1)
          }
        }

        // 更新用户消息状态为失败
        this.updateMessageStatus(userMessage.id, 'failed' as MessageStatus)

        // 失败兜底：清理等待与状态，防止出现在其他角色页面
        if (this.currentDigitalHumanId && this.currentSession?.id) {
          this.removeWaitingReply(
            this.currentDigitalHumanId,
            this.currentSession.id,
            userMessage.id
          )
        }
        this.clearPendingReply()
        if (this.currentDigitalHumanId) {
          this.clearDigitalHumanStates(this.currentDigitalHumanId, {
            loading: false,
            typing: false
          })
        }

        // 添加错误消息
        this.addMessage({
          content: 'Send failed',
          type: 'system' as MessageType,
          status: 'received' as MessageStatus,
          username: 'System'
        })
      } finally {
        // this.setLoading(false);
        this.setTyping(false)
      }
    },

    /**
     * 设置消息内容 - 用于流式消息的最终设置
     */
    async typeMessage(content: string, messageId: string): Promise<void> {
      // 对于流式消息，直接设置最终内容并停止打字状态
      this.updateMessageContent(messageId, content, false)
    },

    /**
     * 清理超时定时器
     */
    clearTimeout() {
      if (this.currentTimeoutId) {
        clearTimeout(this.currentTimeoutId)
        this.currentTimeoutId = null
      }
    },

    /**
     * 🔧 初始化 SSE 连接
     */
    initializeSSE() {
      if (this.sseConnecting || this.sseConnected) {
        console.log('🔌 SSE 已在连接中或已连接，跳过初始化')
        return
      }

      try {
        const sseManager = getSSEManager()

        // 注册事件回调
        sseManager.on({
          onOpen: () => {
            this.sseConnected = true
            this.sseConnecting = false
            this.sseError = null
            console.log('✅ chatStore: SSE 连接成功')
          },
          onMessage: (data: any) => {
            this.handleSSEMessage(data)
          },
          onError: (error: Event) => {
            this.sseConnected = false
            this.sseConnecting = false
            this.sseError = 'SSE 连接错误'
            console.error('❌ chatStore: SSE 连接错误', error)
          },
          onClose: (event: CloseEvent) => {
            this.sseConnected = false
            this.sseConnecting = false
            this.sseError = `SSE 连接关闭 (${event.code})`
            console.log('🔌 chatStore: SSE 连接关闭', event)
          },
          onRetry: (attempt: number) => {
            this.sseConnecting = true
            this.sseConnected = false
            this.sseError = `SSE 正在重连... (第 ${attempt} 次)`
            console.log(`🔄 chatStore: SSE 正在重连... (第 ${attempt} 次)`)
          }
        })

        this.sseConnecting = true
        sseManager.connect()
        console.log('🔌 chatStore: SSE 初始化完成，开始连接')
      } catch (error) {
        console.error('❌ 初始化 SSE 失败:', error)
        this.sseError = 'SSE 初始化失败'
        this.sseConnecting = false
      }
    },

    /**
     * 重新发送失败的消息
     */
    async resendMessage(messageId: string): Promise<void> {
      const session = this.currentSession
      if (!session) return

      const message = session.messages.find((m: any) => m.id === messageId)
      if (message && message.status === 'failed') {
        // 保存消息内容
        const content = message.content

        // 找到失败消息的索引
        const messageIndex = session.messages.findIndex((m: any) => m.id === messageId)
        if (messageIndex !== -1) {
          // 删除失败的用户消息
          session.messages.splice(messageIndex, 1)

          // 🔧 删除紧跟在失败消息后面的系统错误提示（如果存在）
          // 检查下一条消息是否是系统错误提示
          if (messageIndex < session.messages.length) {
            const nextMessage = session.messages[messageIndex]
            if (
              nextMessage &&
              nextMessage.type === 'system' &&
              nextMessage.username === 'System' &&
              (nextMessage.content?.includes('failed') ||
                nextMessage.content?.includes('失败') ||
                nextMessage.content?.includes('error') ||
                nextMessage.content?.includes('错误'))
            ) {
              // 删除系统错误提示消息
              session.messages.splice(messageIndex, 1)
            }
          }
        }

        this.saveToStorage()

        // 再重新发送
        await this.sendMessage(content)
      }
    },

    // 🔧 新增：未读消息管理方法
    /**
     * 增加未读消息数量
     */
    incrementUnreadCount(digitalHumanId: string) {
      const digitalHuman = this.digitalHumans[digitalHumanId]
      if (digitalHuman) {
        digitalHuman.unreadCount = (digitalHuman.unreadCount || 0) + 1

        this.saveToStorage()
      }
    },

    /**
     * 清除指定数字人的未读消息
     */
    clearUnreadCount(digitalHumanId: string) {
      const digitalHuman = this.digitalHumans[digitalHumanId]
      if (digitalHuman && digitalHuman.unreadCount && digitalHuman.unreadCount > 0) {
        console.log(`📬 清除 ${digitalHuman.name} 的 ${digitalHuman.unreadCount} 条未读消息`)
        digitalHuman.unreadCount = 0
        digitalHuman.lastActiveTime = Date.now()
        this.saveToStorage()
      }
    },

    /**
     * 标记当前数字人为已读（切换到该数字人时调用）
     */
    markCurrentAsRead() {
      if (this.currentDigitalHumanId) {
        this.clearUnreadCount(this.currentDigitalHumanId)
      }
    },

    /**
     * 清除所有未读消息
     */
    clearAllUnreadCount() {
      Object.values(this.digitalHumans).forEach(digitalHuman => {
        if (digitalHuman.unreadCount && digitalHuman.unreadCount > 0) {
          digitalHuman.unreadCount = 0
          digitalHuman.lastActiveTime = Date.now()
        }
      })
      console.log('📬 已清除所有未读消息')
      this.saveToStorage()
    },
    /**
     * 清楚最新发送的消息ID
     */
    clearLatestSentMessageId() {
      this.latestSentMessageId = null
    },

    /**
     * 🔧 新增：判断 SSE 是否正在推送消息
     * 如果最近 3 秒内收到过消息，认为正在推送中
     */
    isSSEPushing(): boolean {
      const timeSinceLastMessage = Date.now() - this.lastMessageReceivedTime
      const threshold = 3000 // 3秒阈值
      return timeSinceLastMessage < threshold
    },

    /**
     * 🔧 已禁用：不再使用30秒强制重连定时器
     * 改为依靠心跳超时检测（10秒未收到心跳回复才重连）
     */
    startAutoReconnectTimer() {
      console.log('✅ 心跳检测已启用（由 SSE 管理器负责）')
      // 🔧 已禁用：不再每 30 秒强制断开重连
      // 现在改为：如果 10 秒未收到心跳回复才断开重连
      // 实现位置：src/utils/SSE.ts - startHeartbeat()

      // // 如果已经有定时器在运行，先清除
      // if (this.autoReconnectTimerId) {
      //   console.log('⚠️ 自动重连定时器已在运行，先清除')
      //   clearInterval(this.autoReconnectTimerId)
      //   this.autoReconnectTimerId = null
      // }

      // console.log('🔄 启动30秒自动重连定时器')

      // this.autoReconnectTimerId = setInterval(() => {
      //   try {
      //     // 🔧 优化：直接判断 SSE 是否正在推送消息
      //     if (this.isSSEPushing()) {
      //       console.log('⏸️ SSE 正在推送消息，跳过本次自动重连')
      //       return
      //     }

      //     console.log('🔄 执行30秒定时自动重连（空闲状态）')
      //     const wsManager = getSSEManager()
      //     wsManager.reconnectWithNewToken()
      //   } catch (error) {
      //     console.error('❌ 自动重连失败:', error)
      //   }
      // }, 30000) // 30秒

      // console.log('✅ 30秒自动重连定时器已启动')
    },

    /**
     * 🔧 新增：停止30秒自动重连定时器
     */
    stopAutoReconnectTimer() {
      if (this.autoReconnectTimerId) {
        console.log('🛑 停止30秒自动重连定时器')
        clearInterval(this.autoReconnectTimerId)
        this.autoReconnectTimerId = null
      }
    }
  }
})
